import { fromIso } from '@pf/shared';
import { z } from 'zod';
import { BankAccountSchema, CurrencySchema, DepositStatusSchema, EventIdSchema, MoneySchema, OperatorIdSchema, PaginationQuerySchema, ReviewCaseIdSchema, TransactionIdSchema, paginatedResponseSchema, } from './common.js';
import { AmountAdjustmentSchema, CheckMatrixSchema, VerifiedSlipSchema } from './verification.js';
import { EventTypeSchema } from './webhooks.js';
/**
 * The operator API, `/ops/v1` (SAD §8 "Plus the operator API under `/ops/v1`", §11 RBAC, M-01,
 * M-06, M-07, M-05): review queue, dual-control decisions, participants, key rotation, the
 * webhook DLQ, and policy tunables.
 *
 * SAD §8/§11 describe this surface by name and by role, not endpoint-by-endpoint the way `/v1`
 * is — every schema below is therefore a judgment call built from M-01/M-06/M-07/M-05's
 * interfaces and SAD §11's RBAC table. See this package's delivery report for the list.
 */
// ---------------------------------------------------------------------------------------------
// Site scope — the console's cross-cutting participant filter
// ---------------------------------------------------------------------------------------------
/**
 * "Show me one website's rows", as a query parameter.
 *
 * Every operator read below is *platform-wide* by design: an operator account belongs to the
 * platform, not to a participant, so the pool an operator looks at mixes every site the gateway
 * serves. That is the right default — a queue is worked in the order money arrived, not
 * site-by-site — but it is the wrong *only* option, because a question like "is UFA's deposit
 * flow broken this afternoon?" cannot be asked of a mixed pool at all.
 *
 * Absent means the whole pool, which is what keeps this additive: an existing caller that never
 * learns the param keeps the platform-wide read it has today. Present, it is ANDed with whatever
 * else the endpoint filters by, and an id that matches no participant returns an empty page
 * rather than an error — a filter naming something that no longer exists is a view with nothing
 * in it, not a malformed request.
 *
 * Bounded at 64 characters because that is the width of `participant.id` in the MySQL schema; a
 * longer value could not match a row, so refusing it early keeps a hostile query string out of
 * the query planner.
 */
export const ParticipantFilterSchema = z.string().min(1).max(64).optional();
// ---------------------------------------------------------------------------------------------
// RBAC (SAD §11)
// ---------------------------------------------------------------------------------------------
/**
 * Operator roles (SAD §11): "Viewer (read) · Maker (propose) · Checker (confirm, dual control) ·
 * Admin (participants/keys — never touches individual money state)."
 *
 * `SUPER` (2026-08-05, owner decision) sits above the SAD §11 four: it holds every permission at
 * once, and it may propose *and* resolve an above-threshold review case in a single action — a
 * business-accepted weakening of the four-eyes control, loudly audited via the
 * `review.super_decide` operator action.
 */
export const OPERATOR_ROLES = ['VIEWER', 'MAKER', 'CHECKER', 'ADMIN', 'SUPER'];
export const RoleSchema = z.enum(OPERATOR_ROLES);
// ---------------------------------------------------------------------------------------------
// Operator accounts (SAD §11 — console operator management, Admin-only)
// ---------------------------------------------------------------------------------------------
/**
 * Operator account lifecycle. There is no deleted state: `operator` rows are FK-referenced by
 * review cases and the audit trail, so offboarding is `SUSPENDED` — the account stops
 * authenticating (enforced on the *next request*, not at session expiry) but its history stays
 * attributable.
 */
export const OperatorStatusSchema = z.enum(['ACTIVE', 'SUSPENDED']);
/**
 * The handle an operator signs in with. Deliberately not an email address: console accounts are
 * provisioned by an admin, not self-registered, so there is no address to verify and no reason to
 * put one in the audit trail. Matching is case-insensitive (see the `LOWER(username)` unique
 * index), which is why the charset stays narrow enough to have no case-folding surprises.
 */
export const UsernameSchema = z
    .string()
    .min(3)
    .max(64)
    .regex(/^[A-Za-z0-9._-]+$/, 'letters, digits, dot, underscore or hyphen only');
/**
 * A console operator account, as the management screen sees it. Never carries the password hash
 * or the sealed TOTP secret — `totp_enrolled` is the only fact about the second factor anyone
 * outside the login path needs.
 */
export const OperatorAccountSchema = z
    .object({
    id: OperatorIdSchema,
    username: UsernameSchema,
    display_name: z.string().min(1).max(255),
    role: RoleSchema,
    status: OperatorStatusSchema,
    totp_enrolled: z.boolean(),
    last_login_at: z.string().datetime({ offset: true }).nullable(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
})
    .strict();
/** `200` response for `GET /ops/v1/operators`. */
export const OperatorsListResponseSchema = z
    .object({ items: z.array(OperatorAccountSchema) })
    .strict();
/**
 * Request body for `POST /ops/v1/operators`.
 *
 * The initial password is set by the admin and handed over out of band; the account is expected
 * to change it. 8 is the floor NIST 800-63B draws for user-chosen secrets without composition
 * rules.
 */
export const CreateOperatorRequestSchema = z
    .object({
    username: UsernameSchema,
    display_name: z.string().min(1).max(255),
    role: RoleSchema,
    password: z.string().min(8).max(128),
})
    .strict();
/**
 * Request body for `PATCH /ops/v1/operators/{id}` — a partial update; omitted fields are
 * unchanged. Setting `password` resets the credential (the target's sessions are not revoked by
 * this contract; suspension is the tool for locking someone out *now*).
 */
export const UpdateOperatorRequestSchema = z
    .object({
    display_name: z.string().min(1).max(255).optional(),
    role: RoleSchema.optional(),
    status: OperatorStatusSchema.optional(),
    password: z.string().min(8).max(128).optional(),
})
    .strict();
/** Path parameters for the operator-account endpoints. */
export const OperatorIdParamSchema = z.object({ id: OperatorIdSchema }).strict();
// ---------------------------------------------------------------------------------------------
// Review queue (M-06 ReviewService)
// ---------------------------------------------------------------------------------------------
/**
 * Why a case was opened. Combines the SAD §6 `VerificationDecision` REVIEW reasons with the
 * other UNDER_REVIEW triggers in the SAD §4 state table (evidence deadline breach, credit
 * failure, late slip, provider outage, attempts exhausted with no evidence at all).
 */
export const REVIEW_REASONS = [
    'NAME_BAND',
    'AMOUNT_MISMATCH',
    'INCOMPLETE_DATA',
    'STALE_TIMESTAMP',
    'ATTEMPTS_EXHAUSTED',
    'EVIDENCE_DEADLINE_BREACHED',
    'CREDIT_FAILED',
    'LATE_SLIP',
    'VERIFICATION_AMBIGUOUS',
    'PROVIDER_DOWN',
    /** The slip's sender did not match the reservation's `payer_account` (verify-the-sender). */
    'SENDER_MISMATCH',
    /**
     * Cross-participant match only: the slip's sender looks like the expected payee — the same
     * person may hold accounts on both websites and be settling their own withdrawal. The
     * no-self-match rule cannot see it, because `withdrawer_account_ref` is per-website; the slip
     * can, because the sender and the payee appear on it together. REVIEW rather than a
     * fraud-class REJECT: common Thai names collide, and an operator decides.
     */
    'SELF_MATCH_SUSPECTED',
    'SLIP_ALREADY_CLAIMED',
    /**
     * The slip's `trans_ref` is already held by another order on a different withdrawal, and the
     * `duplicate_trans_ref_auto_reject` policy is off — so instead of the fraud-class REJECT below,
     * an operator decides. The earlier order may already have been credited for this transfer.
     */
    'DUPLICATE_SLIP',
    /**
     * SAD §6 fraud-class REJECT (a replayed `trans_ref` — the sole auto-reject since the
     * `NOT_A_SLIP` arm was routed to review, 2026-08-04): the claim itself is
     * discredited, not merely ambiguous. Opened as a *shadow* case — the domain never lets an
     * operator decide it, it exists so the residual risk is observable — but it is reachable
     * directly by id (`GET /ops/v1/review-cases/{id}`), unlike the triage queue, which excludes it.
     * A distinct value rather than folding it into `VERIFICATION_AMBIGUOUS`: that reads as "we
     * could not tell", which is not what happened here.
     */
    'FRAUD_CLASS_REJECT_SHADOW',
];
export const ReviewReasonSchema = z.enum(REVIEW_REASONS);
/** Lifecycle of a review case: opened, a maker has proposed, a checker has confirmed, or closed. */
export const ReviewCaseStatusSchema = z.enum(['OPEN', 'PROPOSED', 'CONFIRMED', 'CLOSED']);
/**
 * A maker's or checker's verdict on a case. `EXTERNAL`: the case was settled manually outside
 * the credit rails — the money moved by hand, not by a PaymentFlow credit instruction. Dual
 * control is identical to APPROVE; the transaction is flagged `settled_externally` on every
 * read model so the website's recon backstop skips crediting it.
 *
 * `APPROVE_ADJUSTED` ("เกลี่ยยอด"): approve at what the slip proves rather than what was
 * reserved. The server computes the adjustment from the attested slip — the client never sends
 * amounts — and the resulting `transaction.confirmed` carries `withdrawer_adjustment` exactly as
 * the `amountAutoReconcile*` path would. Dual control is identical to APPROVE. Proposable only
 * when the case detail carries a non-null `adjustment_preview`; otherwise the server refuses
 * with 422 `ADJUSTMENT_NOT_APPLICABLE`.
 *
 * `REVERIFY`: the case is closed by sending the transaction back through verification, not by
 * deciding it. It exists for cases opened `PROVIDER_DOWN` — the provider could not be asked, so
 * there is no doubtful answer to adjudicate, only an unasked question. It moves no money: nothing
 * is credited, no funds lock is released or committed, and the eventual outcome arrives on the
 * ordinary verification events. Because it is not a money decision it is deliberately **outside**
 * dual control and outside the maker/checker path — see `ProposeReviewDecisionRequestSchema`,
 * whose enum excludes it, and `POST /ops/v1/review-cases/{id}/reverify`, which is the only way to
 * reach it.
 *
 * Named `ReviewCaseDecision`, not `ReviewDecision` — `verification.ts` already exports a
 * `ReviewDecisionSchema` (the SAD §6 matrix's REVIEW arm) and both files re-export from the
 * package root.
 */
export const ReviewCaseDecisionSchema = z.enum([
    'APPROVE',
    'REJECT',
    'EXTERNAL',
    'APPROVE_ADJUSTED',
    'REVERIFY',
]);
const reviewProposalSchema = z
    .object({
    maker: OperatorIdSchema,
    decision: ReviewCaseDecisionSchema,
    note: z.string().min(1).max(2000),
    proposed_at: z.string().datetime({ offset: true }),
})
    .strict();
const reviewConfirmationSchema = z
    .object({
    checker: OperatorIdSchema,
    confirmed_at: z.string().datetime({ offset: true }),
})
    .strict();
/** A review-queue list row — lighter than the case detail view. */
export const ReviewCaseSummarySchema = z
    .object({
    id: ReviewCaseIdSchema,
    transaction_id: TransactionIdSchema,
    reason: ReviewReasonSchema,
    status: ReviewCaseStatusSchema,
    opened_at: z.string().datetime({ offset: true }),
})
    .strict();
/**
 * The bank account the participant said its customer would pay from — verify-the-sender.
 *
 * Shaped like {@link BankAccountSchema} plus `last4`, since the review screen compares last-4
 * directly against the slip's disclosed sender the same way it already does for the receiver.
 */
export const ExpectedPayerSchema = z
    .object({
    bank: z.string().min(2).max(20),
    name: z.string().min(1).max(255),
    /** Masked account number — never the full number (SAD §5 P9-adjacent: minimise exposure). */
    number: z.string().min(1).max(64),
    last4: z.string().length(4),
})
    .strict();
/**
 * The order that already holds this case's bank reference — the duplicate-slip backlink.
 *
 * A `trans_ref` is unique across the whole system (SAD §6). When a second slip attests one that
 * is taken, the losing row keeps its attestation but no claim, and until this field existed the
 * *winner* was reachable only by querying the evidence table by hand — which is how the two live
 * cases that motivated it were diagnosed.
 *
 * `transaction_id` and `settlement_id` are mutually exclusive and exactly one is non-null: the
 * uniqueness index spans both owner kinds, so a depositor's slip can collide with a settlement's.
 * The console links to a transaction and states the settlement rather than linking to a screen
 * that does not exist.
 */
export const DuplicateSlipClaimSchema = z
    .object({
    /** The contested bank reference. */
    trans_ref: z.string().min(1).max(255),
    /** The deposit holding it, or `null` when a settlement does. */
    transaction_id: TransactionIdSchema.nullable(),
    /** The settlement holding it, or `null` when a deposit does. */
    settlement_id: z.string().min(1).max(64).nullable(),
    participant_id: z.string().min(1).max(64),
    /** When the holding row recorded its attestation. */
    claimed_at: z.string().datetime({ offset: true }).nullable(),
    /**
     * The holding order's state, `null` when it could not be read back — a settlement's slip, or
     * a row this server no longer has. The link is still worth showing without it.
     */
    status: DepositStatusSchema.nullable(),
    amount: MoneySchema.nullable(),
    /** The payout the holding order was matched to. */
    withdrawal_id: z.string().min(1).max(64).nullable(),
    /** The holding order's depositor, as the participant identifies them. */
    depositor_account_ref: z.string().min(1).max(255).nullable(),
    created_at: z.string().datetime({ offset: true }).nullable(),
    /**
     * Whether both orders compete for the *same* payout — the line between
     * `SLIP_ALREADY_CLAIMED` (the depositor's own dead order, re-sent) and a fraud-class REJECT
     * (somebody else's slip). `false` also covers "could not be established", which is why the
     * console words it as a question rather than an accusation either way.
     */
    same_withdrawal: z.boolean(),
})
    .strict();
/**
 * Full review case detail: the slip viewer's expected-vs-actual payee diff, plus maker/checker
 * dual-control state (M-06 `ReviewService`).
 */
export const ReviewCaseDetailSchema = z
    .object({
    id: ReviewCaseIdSchema,
    transaction_id: TransactionIdSchema,
    reason: ReviewReasonSchema,
    status: ReviewCaseStatusSchema,
    opened_at: z.string().datetime({ offset: true }),
    expected_payee: BankAccountSchema,
    /**
     * The reservation's declared payer account (verify-the-sender), `null` when none was ever
     * supplied — a reservation predating the feature, or a participant that has not adopted it.
     * Optional for the same reason: an older server or a cached response may not carry it at
     * all, and that is not the same fact as "we checked and there wasn't one".
     */
    expected_payer: ExpectedPayerSchema.nullable().optional(),
    /**
     * Fuzzy similarity (SAD §6) between the slip's disclosed sender name and
     * {@link expected_payer}'s registered name — the ต้นทาง counterpart of
     * `ReviewCaseContextSchema.name_score`, which scores the *receiver*.
     *
     * `null` when nothing could be scored: no `payer_account`, no slip, or a slip that disclosed
     * no sender name. Optional for the same legacy/compat reason as `expected_payer` — an older
     * server does not send the key at all, and that is not the same fact as "we compared and
     * there was no score".
     *
     * It rides on the *detail* rather than on the review-queue context because, unlike the
     * receiver's score, it is not persisted: it is recomputed from the two names this response
     * already carries.
     */
    payer_name_score: z.number().min(0).max(1).nullable().optional(),
    /**
     * When the reservation was made, so the console can flag a slip whose `transferred_at`
     * predates it — a slip that could not possibly be evidence of *this* payment. Optional for
     * the same legacy/compat reason as `expected_payer`.
     */
    reserved_at: z.string().datetime({ offset: true }).optional(),
    slip: VerifiedSlipSchema.nullable(),
    /**
     * The check matrix behind {@link slip}, from the same evidence attempt — so the console can
     * render the field-by-field verdict without a second round trip through
     * `GET /ops/v1/transactions/{id}`, whose own projection can legitimately be `null` for a
     * case this one is not (see `GetReviewCaseDetail`). Optional/nullable: absent on an older
     * server, `null` when no attempt was ever attested.
     */
    checks: CheckMatrixSchema.nullable().optional(),
    /**
     * The adjustment an `APPROVE_ADJUSTED` decision would settle ("เกลี่ยยอด"), computed
     * server-side from the attested slip against the reserved amount:
     * `{ direction, paid_amount, reserved_amount, delta }` — the same shape
     * `transaction.confirmed` carries as `withdrawer_adjustment`.
     *
     * `null` when no adjustment is possible (no attested slip, exact
     * match, zero amount, or a cross-website match), which is also exactly when proposing
     * `APPROVE_ADJUSTED` is refused — the console shows the option iff this is present.
     * Optional for the older-server reason `checks` is.
     */
    adjustment_preview: AmountAdjustmentSchema.nullable().optional(),
    /**
     * The order that already claimed {@link slip}'s `trans_ref`, or `null` when this transaction
     * holds it itself — which is what every ordinary case looks like. Optional for the
     * older-server reason `checks` is.
     */
    duplicate_of: DuplicateSlipClaimSchema.nullable().optional(),
    /**
     * Whether `POST /ops/v1/review-cases/{id}/reverify` would be accepted. Optional because an
     * older server does not send it; the console then falls back to the case's reason.
     */
    reverifiable: z.boolean().optional(),
    proposal: reviewProposalSchema.nullable(),
    confirmation: reviewConfirmationSchema.nullable(),
})
    .strict();
/** Query parameters for `GET /ops/v1/review-cases`. */
export const ReviewQueueQuerySchema = PaginationQuerySchema.extend({
    status: ReviewCaseStatusSchema.optional(),
    reason: ReviewReasonSchema.optional(),
}).strict();
/** `200` response for `GET /ops/v1/review-cases`. */
export const ReviewQueueResponseSchema = paginatedResponseSchema(ReviewCaseSummarySchema);
// ---------------------------------------------------------------------------------------------
// GET /ops/v1/review-queue — the triage projection (M-12)
// ---------------------------------------------------------------------------------------------
/**
 * The context a triaging operator needs that {@link ReviewCaseSummarySchema} deliberately does
 * not carry.
 *
 * `ReviewCaseSummary` is `.strict()` and holds exactly five fields — correct for a contract that
 * describes the *case*, and useless as a queue: nobody can triage without the amount, the age
 * against SLA, or who is already working it. Rather than widen the frozen summary, this rides
 * alongside as a read model, which is the pattern SAD §13 prescribes ("dashboards are projections
 * off the event stream").
 *
 * It lived in `apps/console` until the operator API grew the endpoint that serves it. Console-local
 * was the wrong home for the same reason the Cashier Profile was: a shape two systems must agree
 * on is a contract, whoever happens to render it.
 */
export const ReviewCaseContextSchema = z
    .object({
    case_id: ReviewCaseIdSchema,
    amount: MoneySchema,
    /** Deadline the SAD §4 review SLA gives this case. */
    sla_due_at: z.string().datetime({ offset: true }),
    /** Operator currently working the case, or `null` if unclaimed. */
    claimed_by: z.string().min(1).nullable(),
    claimed_by_id: OperatorIdSchema.nullable(),
    /** How many slips the depositor submitted before the case opened. */
    evidence_attempts: z.number().int().nonnegative(),
    /**
     * Normalized fuzzy name similarity (SAD §6), `null` when no slip was ever verified — or when
     * the row predates migration 0014, which is when PaymentFlow started keeping it.
     */
    name_score: z.number().min(0).max(1).nullable(),
    /** True when SAD §11 dual control applies because the amount is over threshold. */
    dual_control_required: z.boolean(),
    /** Participant that owns the transaction — the *depositor's* website. */
    participant: z.string().min(1),
    /**
     * The participant that owns the *matched withdrawal* — the withdrawer's website.
     *
     * Since cross-website matching, `participant` above no longer names both sides of a case: a
     * deposit on `web_a` can be paying out a withdrawal owned by `web_b`, and an operator reading
     * one id could not tell which. Read off `withdrawal_request.participant_id` through the
     * transaction's `matched_withdrawal_id`.
     *
     * `null` when the case's transaction has no match (a case opened on an unmatched deposit);
     * equal to `participant` on the ordinary same-website case, which is what the console renders
     * calmly. Optional on the wire for the same pre-upgrade reason as `account_ref`: the deployed
     * console must keep parsing a response that does not carry the key.
     */
    withdrawal_participant: z.string().min(1).nullable().optional(),
    /**
     * The depositor's `account_ref` — the participating website's own username for the customer
     * behind the deposit, and the identity the abandonment report ranks by. On the queue so an
     * operator can recognise a repeat visitor (and paste the ref into History's search) without
     * opening the case. Optional on the wire while pre-upgrade APIs still serve the projection,
     * so a console deployed ahead of the API renders "—" instead of failing the page.
     */
    account_ref: z.string().min(1).optional(),
    /**
     * The website's *display* handle for the depositor — the human-readable username, from the
     * `account_directory` sidecar (migration 0032), latest write winning. Null until the
     * website first sends `account_display`; optional on the wire for the same pre-upgrade
     * reason as `account_ref`. The console shows this and falls back to the ref.
     */
    account_display: z.string().min(1).nullable().optional(),
    /**
     * The matched withdrawal's customer — the withdrawer's per-website ref and display handle,
     * same pair and same rules as the depositor's `account_ref`/`account_display` above, read off
     * the join that already supplies `withdrawal_participant`. A case decides a payout *to* this
     * person, and the card that names the depositor should name them too. Both null when the
     * case's transaction has no match; the ref is also null on a withdrawal pooled before
     * migration 0020 stored it. Optional on the wire for the usual pre-upgrade reason.
     */
    withdrawer_account_ref: z.string().min(1).nullable().optional(),
    withdrawer_account_display: z.string().min(1).nullable().optional(),
})
    .strict();
/** One row of the review-queue projection: contract summary + read-model context. */
export const ReviewQueueRowSchema = z
    .object({ case: ReviewCaseSummarySchema, context: ReviewCaseContextSchema })
    .strict();
/** Query parameters for `GET /ops/v1/review-queue`. Every filter is optional and ANDed. */
export const ReviewQueueProjectionQuerySchema = z
    .object({
    /** A `ReviewCaseStatus`, or `ALL`/absent. */
    status: z.string().optional(),
    /** A `ReviewReason`, or `ALL`/absent. */
    reason: z.string().optional(),
    /** `ALL`, `CLAIMED` or `UNCLAIMED`. */
    claimed: z.string().optional(),
    /** Minimum amount as a decimal string. */
    min_amount: z.string().optional(),
    /**
     * Exact amount as a decimal string — matches the case's transaction amount to the satang.
     * Distinct from `min_amount` (a floor for triage-by-size): this one answers "find the ฿185
     * case", pasted straight off a site's own statement. Both may be sent; they AND.
     */
    amount: z.string().optional(),
    /** Free-text search over case and transaction ids. */
    q: z.string().optional(),
    /** One website's cases, or absent for every site's. See {@link ParticipantFilterSchema}. */
    participant: ParticipantFilterSchema,
})
    .strict();
/** `200` response for `GET /ops/v1/review-queue`. */
export const ReviewQueueProjectionSchema = z
    .object({
    items: z.array(ReviewQueueRowSchema),
    total: z.number().int().nonnegative(),
    /** Cases already past their SLA, across the whole queue rather than this page. */
    breached: z.number().int().nonnegative(),
})
    .strict();
/**
 * `POST` / `DELETE /ops/v1/review-cases/{id}/claim` — take or release a case.
 *
 * Advisory, not a lock: it stops two operators unknowingly working the same case, and it is not
 * what stops them both *deciding* it. That is dual control plus the optimistic `(status, version)`
 * guard on the case itself, neither of which consults a claim.
 */
export const ClaimReviewCaseRequestSchema = z.object({}).strict();
/** Path parameters for the review-case-scoped endpoints. */
export const ReviewCaseIdParamSchema = z.object({ id: ReviewCaseIdSchema }).strict();
/**
 * Request body for `POST /ops/v1/review-cases/{id}/propose` (M-06 `ReviewService.propose`).
 * Requires the `MAKER` role.
 */
export const ProposeReviewDecisionRequestSchema = z
    .object({
    /**
     * See {@link ReviewCaseDecisionSchema} — `EXTERNAL` follows the exact dual-control path
     * APPROVE does.
     *
     * `REVERIFY` is excluded here on purpose. It is a *close outcome*, not a proposal: it decides
     * nothing about the money, it re-asks the provider, and it must not acquire a maker/checker
     * ceremony by being reachable through the decision path. Its endpoint is
     * `POST /ops/v1/review-cases/{id}/reverify`.
     */
    decision: ReviewCaseDecisionSchema.exclude(['REVERIFY']),
    note: z.string().min(1).max(2000),
})
    .strict();
/**
 * Request body for `POST /ops/v1/review-cases/{id}/confirm` (M-06 `ReviewService.confirm`).
 * Requires the `CHECKER` role, and `checker !== maker` — violating either is a 422
 * `DUAL_CONTROL_VIOLATION` (`@pf/shared`'s `DualControlViolationError`). The confirming
 * operator's identity comes from the authenticated session, not the body.
 */
export const ConfirmReviewDecisionRequestSchema = z.object({}).strict();
/**
 * Request body for `POST /ops/v1/review-cases/{id}/reverify` — empty, exactly like
 * {@link ConfirmReviewDecisionRequestSchema}.
 *
 * There is nothing for the caller to say. The operator's identity comes from the authenticated
 * session, the case names the transaction, and the outcome is not the operator's to choose: this
 * endpoint re-asks the slip provider a question that was never asked (a case opened `PROVIDER_DOWN`
 * because the circuit was open), and the provider's answer decides the money on the ordinary
 * verification path. No note field, because there is no judgment to record — a `REVERIFY` close is
 * an admission that we never got an answer, not an opinion about one.
 */
export const ReverifyReviewCaseRequestSchema = z.object({}).strict();
// ---------------------------------------------------------------------------------------------
// Participants (M-07 Directory)
// ---------------------------------------------------------------------------------------------
export const ParticipantStatusSchema = z.enum(['ACTIVE', 'SUSPENDED']);
/** A registered participant website (M-07 Directory). Requires the `ADMIN` role to mutate. */
export const ParticipantSchema = z
    .object({
    id: z.string().min(1).max(128),
    name: z.string().min(1).max(255),
    status: ParticipantStatusSchema,
    /**
     * Which withdrawal pool this website shares, by name (clearing pools, 0037). `null` is a
     * pool of one: this website never cross-matches, whatever `capability_clearing` says.
     *
     * `.optional()` is deploy-order compatibility, not semantics: the console validates every
     * response against this schema and throws on a missing required key, so a required field
     * here cannot be rolled out — new console against old API dies on `Required`. Optional lets
     * the console ship first and read an omitted key as `null`; the API always sends it.
     * Tighten to required one release after both sides carry it.
     */
    clearing_group: z.string().min(1).max(64).nullable().optional(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
})
    .strict();
/** Request body for `POST /ops/v1/participants`. */
export const CreateParticipantRequestSchema = z
    .object({
    id: z.string().min(1).max(128),
    name: z.string().min(1).max(255),
    /** The clearing pool to onboard into. Absent = a pool of one, the safe default. */
    clearing_group: z.string().min(1).max(64).optional(),
})
    .strict();
/** Request body for `PATCH /ops/v1/participants/{id}`. */
export const UpdateParticipantRequestSchema = z
    .object({
    name: z.string().min(1).max(255).optional(),
    status: ParticipantStatusSchema.optional(),
    /**
     * Move the website between clearing pools, or `null` back to a pool of one. Takes effect on
     * the next claim. Distinct from omitting the field, which leaves the pool untouched.
     */
    clearing_group: z.string().min(1).max(64).nullable().optional(),
})
    .strict();
/** Query parameters for `GET /ops/v1/participants`. */
export const ParticipantsQuerySchema = PaginationQuerySchema;
/**
 * How the withdrawal pools are arranged right now.
 *
 * The first question an operator asks about a registered website is whether its depositors are
 * matched against its own withdrawals or against everybody's — it decides who owes whom, and
 * whether one website's liquidity problem is anyone else's. The directory could not answer it:
 * it lists names and statuses, and pooling is not a property of a row.
 *
 * Since 0037 the topology IS per-website: each row's `clearing_group` names the pool it shares,
 * and `null` is a pool of one. What stays global is the kill-switch — `capability_clearing` off
 * means nobody cross-matches, whatever the groups say — so this report is the switch, and the
 * per-row `clearing_group` on {@link ParticipantSchema} is the topology under it.
 */
export const PoolModeSchema = z
    .object({
    /** `true` when cross-matching is enabled at all — the live `capability_clearing`. */
    shared: z.boolean(),
    /**
     * How many ACTIVE participants can actually cross-match: in a named pool with at least one
     * other ACTIVE member. `0` under `shared: true` means the switch is on with every website
     * still in a pool of one — nobody is exposed to anybody.
     */
    participants: z.number().int().nonnegative(),
})
    .strict();
/** `200` response for `GET /ops/v1/participants`. */
export const ParticipantsListResponseSchema = paginatedResponseSchema(ParticipantSchema).extend({
    pool: PoolModeSchema,
});
/** Path parameters for the participant-scoped endpoints. */
export const ParticipantIdParamSchema = z.object({ id: z.string().min(1).max(128) }).strict();
// ---------------------------------------------------------------------------------------------
// Key rotation (M-07 Directory — overlapping-key rotation)
// ---------------------------------------------------------------------------------------------
export const SigningKeyStatusSchema = z.enum(['ACTIVE', 'RETIRING', 'REVOKED']);
/**
 * Which direction a key serves. The two are never interchangeable: `REQUEST_SIGNING` verifies what
 * the website sends *to* PaymentFlow, `WEBHOOK_SIGNING` is what PaymentFlow signs deliveries *to*
 * the website with, and they are separate rows with separate secrets.
 */
export const SigningKeyPurposeSchema = z.enum(['REQUEST_SIGNING', 'WEBHOOK_SIGNING']);
/**
 * A participant's signing key (SAD §11: "a participant may have two active signing keys with
 * distinct key_ids; inbound verification accepts either, outbound signing uses the newest").
 * Never carries the secret itself outside of `RotateKeyResponseSchema`, which discloses it once.
 *
 * `purpose` is published because the list mixes both directions and the key ids are opaque. An
 * operator looking at two active keys and one revoked one cannot otherwise tell whether they are
 * looking at a rotation in progress or at the two halves of a working integration — and the
 * consequence of guessing wrong is revoking the key that signs outbound deliveries.
 */
export const SigningKeySchema = z
    .object({
    key_id: z.string().regex(/^whk_\w+$/),
    purpose: SigningKeyPurposeSchema,
    status: SigningKeyStatusSchema,
    created_at: z.string().datetime({ offset: true }),
})
    .strict();
/** `200` response for `GET /ops/v1/participants/{id}/keys`. */
export const SigningKeyListResponseSchema = z.object({ items: z.array(SigningKeySchema) }).strict();
/**
 * A registered payee account and the aliases SAD §6 fuzzy-matches a slip's receiver name against.
 *
 * Read across every participant by `GET /ops/v1/payee-accounts`. The per-participant variant
 * (`GET /ops/v1/participants/{id}/payee-accounts`) already existed; the console's Admin screen
 * wants the directory as a whole, because a name that fails to match is a directory problem before
 * it is a participant problem.
 */
export const PayeeAccountSchema = z
    .object({
    id: z.string().min(1),
    participant: z.string().min(1),
    account: BankAccountSchema,
    status: z.enum(['ACTIVE', 'DISABLED']),
    /** Registered display aliases the SAD §6 name matcher scores against. */
    aliases: z.array(z.string().min(1)),
    created_at: z.string().datetime({ offset: true }),
})
    .strict();
/** `200` response for `GET /ops/v1/payee-accounts`. */
export const PayeeAccountListResponseSchema = z
    .object({ items: z.array(PayeeAccountSchema) })
    .strict();
/**
 * Query for `GET /ops/v1/payee-accounts` — the cross-participant directory.
 *
 * The endpoint exists precisely *because* the per-participant listing already does: an operator
 * asking "did this slip's payee name match anything at all?" is asking about the directory whole.
 * Naming a participant narrows it back down to the one site, which is the same read
 * `GET /ops/v1/participants/{id}/payee-accounts` serves — reached from the directory screen
 * rather than from a participant's own page.
 */
export const PayeeAccountsQuerySchema = z.object({ participant: ParticipantFilterSchema }).strict();
/** `POST /ops/v1/participants/{id}/keys` takes no body — mints a new overlapping key. */
export const RotateKeyRequestSchema = z.object({}).strict();
/** `201` response for `POST /ops/v1/participants/{id}/keys` — the secret is shown exactly once. */
export const RotateKeyResponseSchema = z
    .object({
    key_id: z.string().regex(/^whk_\w+$/),
    secret: z.string().min(32),
    created_at: z.string().datetime({ offset: true }),
})
    .strict();
/** Path parameters for `DELETE /ops/v1/participants/{id}/keys/{key_id}` (revoke). */
export const RevokeKeyParamSchema = z
    .object({
    id: z.string().min(1).max(128),
    key_id: z.string().regex(/^whk_\w+$/),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// Webhook DLQ (M-05 WebhookDispatcher)
// ---------------------------------------------------------------------------------------------
/** One dead-lettered delivery (SAD §9: attempt 8 exhausted → DLQ + alert). */
export const DeadLetterEntrySchema = z
    .object({
    delivery_id: z.string().regex(/^whd_[0-9A-HJKMNP-TV-Z]{26}$/),
    event_id: EventIdSchema,
    event_type: EventTypeSchema,
    participant: z.string().min(1),
    attempts: z.number().int().positive(),
    last_error: z.string().nullable(),
    dead_lettered_at: z.string().datetime({ offset: true }),
})
    .strict();
/** Query parameters for `GET /ops/v1/dlq`. */
export const DlqQuerySchema = PaginationQuerySchema.extend({
    participant: ParticipantFilterSchema,
}).strict();
/** `200` response for `GET /ops/v1/dlq`. */
export const DlqListResponseSchema = paginatedResponseSchema(DeadLetterEntrySchema);
/** Path parameters for `POST /ops/v1/dlq/{delivery_id}/replay` (M-05 `WebhookDispatcher.replay`). */
export const DlqReplayParamSchema = z
    .object({ delivery_id: z.string().regex(/^whd_[0-9A-HJKMNP-TV-Z]{26}$/) })
    .strict();
/** `POST /ops/v1/dlq/{delivery_id}/replay` takes no body. */
export const DlqReplayRequestSchema = z.object({}).strict();
/** `202` response for `POST /ops/v1/dlq/{delivery_id}/replay`. */
export const DlqReplayResponseSchema = z
    .object({
    delivery_id: z.string().regex(/^whd_[0-9A-HJKMNP-TV-Z]{26}$/),
    status: z.literal('REPLAY_QUEUED'),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// Policy tunables (SAD §16 Q1–Q8, §5 abuse controls, §4 timeouts)
// ---------------------------------------------------------------------------------------------
/**
 * The business-tunable policy config (SAD §4 timeouts table, §5 abuse controls, §16 Q1–Q8).
 * Every field maps to a named open question or default the SAD marks "business-tunable".
 */
export const PolicyConfigSchema = z
    .object({
    /** Q1 — RESERVED with no payment (SAD §4/§5 default ~10 min). */
    reservation_ttl_seconds: z.number().int().positive(),
    /** Q1 — AWAITING_EVIDENCE with no slip (SAD §4/§5 default ~10 min). */
    evidence_deadline_seconds: z.number().int().positive(),
    /** Q1 — VERIFYING max before retry/review (SAD §4 default ~2 min). */
    verification_max_seconds: z.number().int().positive(),
    /** Q1 — UNDER_REVIEW SLA before escalation (SAD §4 default ~24 h). */
    review_sla_seconds: z.number().int().positive(),
    /** Q1 — EXPIRED grace window a late slip may still reopen within (SAD §4 default ~24–48 h). */
    late_slip_grace_seconds: z.number().int().positive(),
    /** Q1 — AVAILABLE max-age before the slot expires (SAD §4 default ~24 h). */
    withdrawal_max_age_seconds: z.number().int().positive(),
    /** Q2 — fuzzy name-match floor for AUTO_APPROVE (SAD §6 default 0.90). */
    name_match_auto_approve_threshold: z.number().min(0).max(1),
    /**
     * Minimum phonetic-skeleton similarity at which a sender name written in one alphabet counts
     * as matching a registered name written in the other — Thai against its romanization. `null`
     * disables the comparison and every cross-script pair keeps going to an operator.
     *
     * Deliberately NOT the same number as `name_match_auto_approve_threshold`: that one scores
     * Jaro-Winkler over characters, this one over consonant classes. Two metrics on two alphabets;
     * one number cannot mean the same thing in both. Optional on the wire for the same deploy-order
     * reason as `same_bank_match_blocked_banks`.
     */
    name_match_cross_script_floor: z.number().min(0).max(1).nullable().optional(),
    /** Q2 — fuzzy name-match floor below which verification is RE_SUBMIT, not REVIEW (SAD §6 default 0.75). */
    name_match_resubmit_floor: z.number().min(0).max(1),
    /** Q8 — max slip re-submission attempts (SAD §5/§6 default 3). */
    max_evidence_resubmit_attempts: z.number().int().positive(),
    /** Q4 — max concurrent reservations per depositor (SAD §5 default 1–3). */
    max_concurrent_reservations_per_depositor: z.number().int().positive(),
    /** Q7 — amount above which review decisions require dual control (SAD §11). */
    dual_control_threshold: z.string().regex(/^\d+(\.\d+)?$/),
    /**
     * Same-bank match gate (SAD §5/§6): banks whose same-bank transfer slips carry no QR.
     *
     * Optional on the wire while pre-gate APIs are still deployed — a console shipped ahead of
     * the API must not fail the whole Settings read over one missing field. Tighten to required
     * once every environment serves it.
     */
    same_bank_match_blocked_banks: z.array(z.string().min(2).max(20)).optional(),
    /**
     * Blocked-payer-bank gate (SAD §5/§6): banks that may not be a depositor's sending bank at
     * all, because their transfers produce no verifiable slip in any direction. Receiving a
     * withdrawal at one of these banks is unaffected.
     *
     * Optional on the wire for the same deploy-order reason as `same_bank_match_blocked_banks`.
     */
    blocked_payer_banks: z.array(z.string().min(2).max(20)).optional(),
    /**
     * Auto-reconcile an under-paid slip: credit the depositor what the slip proves they paid and
     * release the shortfall from the withdrawer's funds lock, instead of opening a review case.
     * Optional on the wire for the same deploy-order reason as `same_bank_match_blocked_banks`.
     */
    amount_auto_reconcile_under: z.boolean().optional(),
    /**
     * Auto-reconcile an over-paid slip: credit the depositor the reserved amount plus as much of
     * the overshoot as the withdrawer's available balance covers, debiting the withdrawer that
     * extra, instead of opening a review case. Optional on the wire, as above.
     */
    amount_auto_reconcile_over: z.boolean().optional(),
    /**
     * Permit amount reconciliation — automatic AND operator-ordered — on a CROSS-WEBSITE match
     * (clearing): depositor credited the paid amount, obligation recorded at the paid amount,
     * withdrawer's website instructed via `withdrawal.fulfilled`. The kill-switch if either
     * side misbehaves, so it must be reachable from this page mid-incident. Optional on the
     * wire, as above.
     */
    amount_auto_reconcile_cross_web: z.boolean().optional(),
    /**
     * Cross-website split fill (0041): let a deposit be served by slicing a pool-member
     * website's withdrawal, after its own pool — whole rows and slices — has nothing. The
     * withdrawal owner's `capability_split_fill` still gates per row. A kill-switch like its
     * reconcile sibling, so it must be reachable from this page. Optional on the wire, as
     * above.
     */
    split_fill_cross_web: z.boolean().optional(),
    /**
     * Split fill: smallest slice as a whole-number percentage of the candidate withdrawal's
     * remaining amount, `null` = rule off. A deposit below this ratio for a candidate skips it —
     * and when every candidate is skipped the reserve is OUT_OF_STOCK, never a nibble at a row
     * ten times its size. Optional on the wire for the same deploy-order reason as
     * `same_bank_match_blocked_banks`; `null` and absent both mean "off" on read.
     */
    split_min_slice_percent: z.number().int().min(1).max(100).nullable().optional(),
    /**
     * Auto-approval (0045): resolve a `SENDER_MISMATCH` case as APPROVE with no human, when the
     * shadow AI reviewer answered APPROVE **and** the slip's disclosed sender account last4
     * matches the registered payer's. Both signals, never either alone — each one by itself paid
     * out cases an operator rejected during the week this was measured on.
     *
     * The kill switch for the only path in the platform where money moves with nobody watching,
     * so like its reconcile siblings it must be reachable from the Settings page mid-incident.
     * Optional on the wire for the same deploy-order reason as `same_bank_match_blocked_banks`.
     */
    ai_auto_approve_sender_mismatch: z.boolean().optional(),
    /**
     * Percentage of otherwise-eligible auto-approve cases still routed to a human, so the rule's
     * error rate keeps being measured once it is live — auto-approval otherwise destroys the very
     * human decisions the comparison is built on. `0` and `null` both mean no holdout.
     */
    ai_auto_approve_holdout_percent: z.number().int().min(0).max(100).nullable().optional(),
    /**
     * Whether a slip whose `trans_ref` is already held by another order (on a different
     * withdrawal) is auto-rejected as fraud-class (SAD §6, `true`), or goes to an operator as a
     * `DUPLICATE_SLIP` review case (`false`). Optional on the wire for the same deploy-order
     * reason as `same_bank_match_blocked_banks`.
     */
    duplicate_trans_ref_auto_reject: z.boolean().optional(),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// GET /ops/v1/positions — Phase 3 clearing exposure (SAD §15, clearing M2)
// ---------------------------------------------------------------------------------------------
/**
 * The net position of one ordered pair of websites: after folding the obligations both ways,
 * `debtor` owes `creditor` `net_amount`. Pairs that net to exactly zero are not listed.
 */
export const ClearingPositionSchema = z
    .object({
    /** The website that owes, once both directions are netted. */
    debtor: z.string().min(1).max(128),
    /** The website that is owed. */
    creditor: z.string().min(1).max(128),
    net_amount: MoneySchema,
    /** How many ledger postings (both directions) fold into this figure. */
    entry_count: z.number().int().positive(),
    /** When the oldest un-settled posting between the pair was made. */
    oldest_at: z.string().datetime({ offset: true }),
    /** When the newest was. */
    latest_at: z.string().datetime({ offset: true }),
})
    .strict();
/**
 * The same exposure as {@link ClearingPositionSchema}, cut by the calendar day it was posted on.
 *
 * A **decomposition of the live position, not a history**: exactly the same un-settled,
 * un-voided postings, so the days of a pair add up to that pair's `net_amount` and a settled
 * position empties its days along with it. Nothing here is a record of past clearing activity —
 * `/ops/v1/settlements` is where a discharged debt goes.
 *
 * `day` is a plain `YYYY-MM-DD` in **Asia/Bangkok**, not a timestamp. Bucketing the stored UTC
 * instants raw would cut every day at 07:00 local and file a Thai evening's matches under
 * tomorrow; carrying the result as a date-time instead would hand the client something it could
 * re-read in another zone and land back on the wrong day. A string can only be printed.
 */
export const ClearingDayPositionSchema = z
    .object({
    /** `YYYY-MM-DD`, Asia/Bangkok. */
    day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** The website that owes, once that day's obligations are netted both ways. */
    debtor: z.string().min(1).max(128),
    /** The website that is owed. */
    creditor: z.string().min(1).max(128),
    net_amount: MoneySchema,
    /** How many ledger postings (both directions) fold into this day's figure. */
    entry_count: z.number().int().positive(),
})
    .strict();
/**
 * `200` response for `GET /ops/v1/positions`.
 *
 * `clearing_enabled` reports the live `capability_clearing` policy value — it is how the console
 * shows whether pools are currently isolated per website (flag off, SAD §14 Phase 1/2) or
 * cross-website matching is active (Phase 3). The positions themselves are served either way:
 * exposure created while the flag was on remains real after it is switched off.
 */
/**
 * One website's exposure netted across every counterparty.
 *
 * `net` is signed: negative means the website owes the group, positive means it is owed. A
 * management view — the limit that actually binds is per pair, because credit risk is bilateral.
 */
export const ParticipantNetSchema = z
    .object({
    participant: z.string().min(1).max(128),
    /** Signed decimal string. The only amount on this API that may be negative. */
    net: z.string().regex(/^-?\d+(\.\d+)?$/),
    currency: CurrencySchema,
    owed_to_it: z.string().regex(/^\d+(\.\d+)?$/),
    it_owes: z.string().regex(/^\d+(\.\d+)?$/),
    counterparties: z.number().int().nonnegative(),
})
    .strict();
/** One transfer in the multilateral netting plan. */
export const SuggestedTransferSchema = z
    .object({
    from: z.string().min(1).max(128),
    to: z.string().min(1).max(128),
    amount: MoneySchema,
})
    .strict();
export const ClearingPositionsResponseSchema = z
    .object({
    clearing_enabled: z.boolean(),
    items: z.array(ClearingPositionSchema),
    /** Each website's exposure netted across every counterparty — a management view. */
    participants: z.array(ParticipantNetSchema),
    /**
     * The same exposure once more, split by the day it was posted, newest day first.
     *
     * Answers what a running total cannot: whether a position is one bad day or a drift that has
     * been building. See {@link ClearingDayPositionSchema} — it sums back to `items`.
     */
    daily: z.array(ClearingDayPositionSchema),
    /**
     * The fewest transfers that would discharge every balance at once (multilateral netting).
     *
     * A plan, not an instruction: settlement is still pair by pair, and moving onto this needs
     * every participant to have agreed that a payment to a third party discharges the original
     * debt. Served so the saving — `items.length` transfers down to this many — is visible.
     */
    suggested_transfers: z.array(SuggestedTransferSchema),
})
    .strict();
/**
 * Query for `GET /ops/v1/positions`.
 *
 * Clearing is the one place where "one website" is not a property of a row: an obligation names
 * *two* participants, a debtor and a creditor, and belongs to neither alone. The filter is
 * therefore read as **involves** — every pair the named site is either side of — which is the
 * only reading under which the netted `participants[]` figure for that site stays true. A filter
 * meaning "owes" would silently drop half of what the site is owed and net to a different number
 * than the unfiltered view, which is the sort of disagreement a clearing screen must not have.
 */
export const ClearingPositionsQuerySchema = z
    .object({ participant: ParticipantFilterSchema })
    .strict();
// ---------------------------------------------------------------------------------------------
// /ops/v1/settlements — netting a position into a real transfer (SAD §15, clearing M3)
// ---------------------------------------------------------------------------------------------
/** Identifier of a settlement (`stl_<ulid>`). */
export const SettlementIdSchema = z.string().regex(/^stl_[0-9A-HJKMNP-TV-Z]{26}$/);
/** Settlement lifecycle. `IN_TRANSIT` is reserved for the slip-pipeline integration. */
export const SettlementStatusSchema = z.enum(['INSTRUCTED', 'IN_TRANSIT', 'COMPLETED', 'FAILED']);
/** One settlement: `debtor` pays `creditor` `net_amount`, covering the window's obligations. */
export const SettlementSchema = z
    .object({
    id: SettlementIdSchema,
    debtor: z.string().min(1).max(128),
    creditor: z.string().min(1).max(128),
    net_amount: MoneySchema,
    status: SettlementStatusSchema,
    /** `created_at` of the oldest and newest obligation folded in. */
    window_start: z.string().datetime({ offset: true }),
    window_end: z.string().datetime({ offset: true }),
    evidence_id: z.string().nullable(),
    created_at: z.string().datetime({ offset: true }),
    completed_at: z.string().datetime({ offset: true }).nullable(),
})
    .strict();
/** `200` response for `GET /ops/v1/settlements`. */
export const SettlementsQuerySchema = z.object({ participant: ParticipantFilterSchema }).strict();
/** `200` for `GET /ops/v1/settlements`. */
export const SettlementsListResponseSchema = z
    .object({ items: z.array(SettlementSchema) })
    .strict();
/**
 * Request body for `POST /ops/v1/settlements` — MAKER only (`settlement:instruct`).
 *
 * Names the direction the operator is looking at; the server re-derives the net under lock and
 * refuses with a 409 when the position moved. Dual control is role disjointness: the CHECKER
 * who concludes a settlement can never be the MAKER who instructed it.
 */
export const InstructSettlementRequestSchema = z
    .object({
    debtor: z.string().min(1).max(128),
    creditor: z.string().min(1).max(128),
    currency: z.string().length(3),
    /**
     * How much is actually being transferred, when that is not the whole net.
     *
     * Absent means "the whole position", which is what every instruction was before this field
     * and what the clearing loop still sends. Present, it is the figure the debtor will really
     * put through the bank — operators settle in round money (฿45,000 against a net of
     * ฿45,536), and the slip they upload afterwards is verified against *this* number, so a
     * settlement that claims the untransferred net could never be certified at all.
     *
     * The difference does not disappear: the remainder is posted straight back to the ledger as
     * live exposure, in whichever direction it falls — under-paying leaves the debtor owing the
     * rest, over-paying (฿46,000 for a ฿45,536 net) makes the creditor owe the change back.
     */
    amount: z
        .string()
        .regex(/^\d+(\.\d+)?$/, 'must be a non-negative decimal string')
        .optional(),
})
    .strict();
/**
 * Request body for `POST /ops/v1/settlements/{id}/complete` and `/fail` — CHECKER only.
 * `evidence_id` is optional until the slip pipeline verifies settlement transfers.
 */
export const DecideSettlementRequestSchema = z
    .object({ evidence_id: z.string().min(1).optional() })
    .strict();
/**
 * Request body for `POST /ops/v1/settlements/{id}/slip` — CHECKER only.
 *
 * Either the decoded QR payload or the key of an uploaded image; the provider takes whichever
 * it is given, exactly as it does for a depositor's slip. Attaching one moves the settlement to
 * `IN_TRANSIT` and hands it to the verification loop — it does not conclude anything, because
 * only a provider `AUTO_APPROVE` may complete a settlement (SAD §6 P4).
 */
export const SubmitSettlementSlipRequestSchema = z
    .object({
    qr_payload: z.string().min(1).optional(),
    object_key: z.string().min(1).optional(),
})
    .strict()
    .refine((body) => body.qr_payload !== undefined || body.object_key !== undefined, {
    message: 'at least one of qr_payload or object_key is required',
});
/** `202` response — the slip is attached and awaiting the provider. */
export const SubmitSettlementSlipResponseSchema = z
    .object({
    evidence_id: z.string().min(1),
    settlement_id: SettlementIdSchema,
    attempt: z.number().int().positive(),
    status: z.literal('IN_TRANSIT'),
})
    .strict();
/** Path parameters for the settlement-scoped endpoints. */
export const SettlementIdParamSchema = z.object({ id: SettlementIdSchema }).strict();
// ---------------------------------------------------------------------------------------------
// GET /ops/v1/metrics — the SAD §13 KPI projection (M-12)
// ---------------------------------------------------------------------------------------------
/**
 * The operator dashboard's KPIs.
 *
 * SAD §13 names these and describes dashboards as "projections off the event stream". They are
 * *also* exported as Prometheus metrics and drawn in Grafana; this is the same information as a
 * versioned JSON document, because a console that had to embed Grafana to show a number would
 * make the operator plane depend on the observability stack being up.
 *
 * Every rate is a series plus the previous window's value, so the screen can render a direction
 * rather than a bare number — "94%" says much less than "94%, down from 98%".
 */
export const MetricPointSchema = z
    .object({ at: z.string().datetime({ offset: true }), value: z.number() })
    .strict();
/** A KPI's current value, the window before it, and the shape in between. */
export const MetricSeriesSchema = z
    .object({ current: z.number(), previous: z.number(), points: z.array(MetricPointSchema) })
    .strict();
/** `200` response for `GET /ops/v1/metrics`. */
export const DashboardMetricsSchema = z
    .object({
    /**
     * Length of the reported window in hours.
     *
     * Fractional for a custom range that does not land on a whole hour. Kept as the window's
     * headline number; `range_start`/`range_end` are the authoritative bounds.
     */
    window_hours: z.number().positive(),
    /** Inclusive start of the reported window. */
    range_start: z.string().datetime({ offset: true }),
    /** Exclusive end of the reported window. Equals `generated_at` for a "last N hours" query. */
    range_end: z.string().datetime({ offset: true }),
    generated_at: z.string().datetime({ offset: true }),
    /** Reservations per second (SAD §13). */
    reservations_per_second: MetricSeriesSchema,
    /** Share of reservations that reached COMPLETED. */
    match_success_rate: MetricSeriesSchema,
    /** Verification outcome split, as percentages summing to 100. */
    verification_outcomes: z
        .object({ pass: z.number(), review: z.number(), reject: z.number() })
        .strict(),
    /** Provider latency percentiles in milliseconds. */
    provider_latency_ms: z
        .object({ p50: MetricSeriesSchema, p95: MetricSeriesSchema, p99: MetricSeriesSchema })
        .strict(),
    /** Share of webhook deliveries acknowledged 2xx without reaching the DLQ. */
    webhook_success_rate: MetricSeriesSchema,
    /** Current dead-letter queue depth. SAD §13: "DLQ is an incident, not a metric." */
    dlq_depth: MetricSeriesSchema,
    /** Open manual-review cases. */
    open_reviews: MetricSeriesSchema,
    /** Share of reservations released back to the pool without payment (abuse signal). */
    reservation_release_rate: MetricSeriesSchema,
    /** Deposit-state census, for the funnel. */
    deposit_state_counts: z.record(z.string(), z.number().int().nonnegative()),
    /** Withdrawal-state census. */
    withdrawal_state_counts: z.record(z.string(), z.number().int().nonnegative()),
    /** Total value settled in the window. */
    settled_volume: MoneySchema,
    /** Provider circuit-breaker state (SAD §6). */
    provider_circuit: z.enum(['CLOSED', 'HALF_OPEN', 'OPEN']),
})
    .strict();
/** Longest window the KPI queries will answer for, in hours (30 days). */
export const METRICS_MAX_WINDOW_HOURS = 720;
/** Shortest window the KPI queries will answer for, in hours — the series buckets hourly. */
export const METRICS_MIN_WINDOW_HOURS = 1;
/**
 * Query parameters for `GET /ops/v1/metrics`.
 *
 * Two ways to name a window, and exactly one of them per request:
 *
 * - `window_hours` — the last N hours, ending now. The presets the console ships with.
 * - `from` (+ optional `to`) — an absolute range. `to` defaults to now, which is what "since
 *   yesterday 08:00" means when an operator types it during an incident.
 *
 * Either way the span is capped at {@link METRICS_MAX_WINDOW_HOURS}: the repository also reads the
 * equal-length window before it to compute deltas, so a request costs twice its span.
 */
export const DashboardMetricsQuerySchema = z
    .object({
    window_hours: z.coerce.number().int().positive().max(METRICS_MAX_WINDOW_HOURS).optional(),
    /** Inclusive start of an absolute range, ISO-8601 with offset. */
    from: z.string().datetime({ offset: true }).optional(),
    /** Exclusive end of an absolute range, ISO-8601 with offset. Defaults to now. */
    to: z.string().datetime({ offset: true }).optional(),
    /**
     * One website's numbers, or absent for the platform's.
     *
     * Every panel the dashboard draws narrows together — deposits, withdrawals, verification
     * outcomes, the review backlog, settled volume — because a half-scoped dashboard is worse
     * than an unscoped one: an operator reading "12 waiting" beside a platform-wide funnel would
     * have no way to tell which number answered which question.
     */
    participant: ParticipantFilterSchema,
})
    .strict()
    .superRefine((query, ctx) => {
    const hasRange = query.from !== undefined || query.to !== undefined;
    if (query.window_hours !== undefined && hasRange) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['window_hours'],
            message: 'window_hours cannot be combined with from/to',
        });
        return;
    }
    if (query.to !== undefined && query.from === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['from'],
            message: 'from is required when to is given',
        });
        return;
    }
    if (query.from === undefined)
        return;
    // `fromIso`, not `new Date`/`Date.parse`: the R5 lint rule bans the `Date` global outside
    // `@pf/shared`'s clock module, and it cannot tell parsing a caller's string from reading the
    // wall clock. Both operands are already `.datetime()`-validated, so neither can be NaN here.
    const from = fromIso(query.from).getTime();
    // `to` absent means "until now", and how long that is only the server knows; the route
    // applies the span bounds once it has resolved the clock.
    if (query.to === undefined)
        return;
    const to = fromIso(query.to).getTime();
    if (to <= from) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: 'to must be after from' });
        return;
    }
    const hours = (to - from) / 3_600_000;
    if (hours < METRICS_MIN_WINDOW_HOURS) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['to'],
            message: `range must span at least ${String(METRICS_MIN_WINDOW_HOURS)} hour`,
        });
    }
    if (hours > METRICS_MAX_WINDOW_HOURS) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['to'],
            message: `range must span at most ${String(METRICS_MAX_WINDOW_HOURS)} hours`,
        });
    }
});
// ---------------------------------------------------------------------------------------------
// GET/PUT /ops/v1/mock/scenario — the slip-provider scenario switch (dev affordance)
// ---------------------------------------------------------------------------------------------
/**
 * The scenario the *mock* slip provider is pinned to.
 *
 * Not part of PaymentFlow's product surface, and deliberately named `mock`: it exists so a
 * reviewer can walk every branch of the SAD §6 decision matrix from the UI instead of curling an
 * admin endpoint. The API refuses it outright when no mock provider is configured, which is what
 * a real deployment looks like.
 */
export const MOCK_SCENARIOS = [
    'SUCCESSFUL',
    'DUPLICATE',
    'TIMEOUT',
    'PROVIDER_FAILURE',
    'BANK_MISMATCH',
    'NAME_MISMATCH',
    'AMOUNT_MISMATCH',
    'DELAYED',
    'INCOMPLETE_DATA',
    'NOT_A_SLIP',
    'STALE_TIMESTAMP',
    'ACCOUNT_MISMATCH',
];
/** One of {@link MOCK_SCENARIOS}. */
export const MockScenarioSchema = z.enum(MOCK_SCENARIOS);
/** `GET`/`PUT /ops/v1/mock/scenario` body and response. */
export const MockScenarioBodySchema = z.object({ scenario: MockScenarioSchema }).strict();
/** Request body for `PUT /ops/v1/policy` — a partial update; omitted fields are unchanged. */
export const UpdatePolicyRequestSchema = PolicyConfigSchema.partial().strict();
// ---------------------------------------------------------------------------------------------
// GET /ops/v1/withdrawals — the withdrawal pool as the operator sees it
// ---------------------------------------------------------------------------------------------
/**
 * One withdrawal in the pool. `number_masked` deliberately, unlike the review detail's full
 * number: this list is a queue overview, not an evidence-comparison surface, and it renders many
 * strangers' accounts at once.
 */
export const WithdrawalQueueRowSchema = z
    .object({
    id: z.string().min(1),
    /** The website this withdrawal belongs to — the *withdrawer's* side, always present. */
    participant: z.string().min(1),
    /**
     * The website the *matched deposit* belongs to — the depositor's side.
     *
     * A withdrawal row already names exactly one participant (its own), which since cross-website
     * matching is only half the pairing: the deposit holding this row may belong to another site.
     * Read off the matched deposit that this queue already resolves for `matched_transaction_id`,
     * so it is present exactly when that id is — i.e. on RESERVED/LOCKED (and on a DONE row that
     * was matched at some point), and `null` on a row that has never left the pool.
     *
     * Optional on the wire for the pre-upgrade reason every field here shares.
     */
    depositor_participant: z.string().min(1).nullable().optional(),
    amount: MoneySchema,
    /**
     * The amount at insert, never rewritten (migration 0034) — same currency as `amount`. Equal
     * to `amount` for a row that has never been split; on a split parent it is the pre-split
     * total, so the console can show `origin_amount → amount` instead of a bare residual that
     * reads as the amount the website locked. Optional on the wire for the pre-upgrade reason
     * every lineage field here shares: a console deployed ahead of the API must still render.
     */
    origin_amount: MoneySchema.optional(),
    /**
     * How many slices have been cut from this row. Zero (and always zero on a slice itself, since
     * a slice is never sliced again) for a row that has never been a split parent. Optional for
     * the pre-upgrade reason above.
     */
    split_count: z.number().int().nonnegative().optional(),
    /**
     * The row this one was cut from, or null for a root row (including an unsplit parent).
     * Optional for the pre-upgrade reason above.
     */
    origin_withdrawal_id: z.string().min(1).nullable().optional(),
    /**
     * This slice's 1-based position within its origin. Null on a root row; optional for the
     * pre-upgrade reason above.
     */
    split_sequence: z.number().int().min(1).nullable().optional(),
    /**
     * The wallet lock this row names on the website — constant across a split, so a slice's own
     * `<this>#<split_sequence>` always resolves back to its origin's value without a lookup.
     * Optional for the pre-upgrade reason above.
     */
    lock_reference: z.string().min(1).optional(),
    bank: z.string().min(1),
    name: z.string().min(1),
    number_masked: z.string().min(1),
    /**
     * The withdrawer's `account_ref` — the participating website's own username for the customer
     * whose money is queued (migration 0020's no-self-match key). Null on legacy rows created
     * before the column existed; optional on the wire while pre-upgrade APIs still serve this
     * list, so a console deployed ahead of the API renders "—" instead of failing the page.
     */
    account_ref: z.string().min(1).nullable().optional(),
    /**
     * The website's *display* handle for the withdrawer — see `ReviewCaseContextSchema`'s
     * `account_display` for the full note. Null until the website sends one; optional on the
     * wire for the pre-upgrade reason.
     */
    account_display: z.string().min(1).nullable().optional(),
    /** The deposit currently (or last) matched to this withdrawal — the click-through target. */
    matched_transaction_id: z.string().nullable(),
    /**
     * When that matched deposit reserved this withdrawal — the instant the row left the pool,
     * and what "reserved for how long" on the in-flight tab is measured from. Row age (from
     * `created_at`) answers a different question and answers it misleadingly here: a row pooled
     * on Monday and reserved a minute ago is not a row anybody needs to chase.
     *
     * It is the matched *deposit's* `created_at`, not a column on the withdrawal, because there
     * is none: `withdrawal_request` carries `reserved_until` (the deadline) and nothing that
     * records when the reservation began. Nor does it need one — a reservation exists precisely
     * because a deposit row was created against this withdrawal, so that row's creation *is* the
     * reservation moment rather than a stand-in for it. Null when no deposit is or was matched;
     * optional on the wire for the pre-upgrade reason every field here shares: a console
     * deployed ahead of the API must still render.
     */
    reserved_at: z.string().datetime({ offset: true }).nullable().optional(),
    /**
     * When the depositor last submitted evidence for that matched deposit — the newest slip's
     * timestamp, because a re-submit after a failed check is a fresh attempt and the last one is
     * the one anybody is waiting on.
     *
     * Null when no slip has been submitted at all, which is the *normal* state of a RESERVED row
     * and exactly the distinction the operator is trying to see: "reserved, still silent" and
     * "reserved, slip already in verification" are indistinguishable from the row's own age.
     * Optional for the pre-upgrade reason above.
     */
    evidence_submitted_at: z.string().datetime({ offset: true }).nullable().optional(),
    /**
     * The matched deposit's current status, so a row that is still in flight can say *why*
     * without the operator opening the deposit to find out. Null when nothing is matched.
     * Optional for the pre-upgrade reason above.
     */
    deposit_status: DepositStatusSchema.nullable().optional(),
    status: z.enum([
        'AVAILABLE',
        'RESERVED',
        'LOCKED',
        'ON_HOLD',
        'FULFILLED',
        'CANCELLED',
        'EXPIRED',
        /**
         * A slice folded back into the withdrawal it was cut from (0047). Lands in the DONE tab
         * with the other terminals, and is the only one of them that returned nothing to the
         * withdrawer — its amount went into the origin row, which is still queued and still owed.
         */
        'MERGED',
    ]),
    /**
     * Who cancelled a `CANCELLED` row — `WITHDRAWER` (the customer, through their website) or
     * `OPERATOR` (an admin, via `POST /ops/v1/withdrawals/{id}/cancel`). Read off the terminal
     * `withdrawal.released` event's `cause`, which is the audit record of the decision. Null on
     * every non-CANCELLED row, and on a CANCELLED row whose event predates the cause being
     * recorded; optional on the wire so a console deployed ahead of the API renders the plain
     * badge instead of failing the page.
     */
    cancelled_by: z.enum(['WITHDRAWER', 'OPERATOR']).nullable().optional(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
})
    .strict();
/** Lifecycle groups the queue filters by; the wire statuses partition into exactly these. */
export const WITHDRAWAL_QUEUE_GROUPS = ['WAITING', 'IN_FLIGHT', 'DONE'];
/** Query for `GET /ops/v1/withdrawals`. */
export const WithdrawalQueueQuerySchema = z
    .object({
    group: z.enum(['WAITING', 'IN_FLIGHT', 'DONE', 'ALL']).default('WAITING'),
    /** Narrows the group to one wire status — the IN_FLIGHT and DONE tabs each mix several. */
    status: z
        .enum([
        'ALL',
        'AVAILABLE',
        'RESERVED',
        'LOCKED',
        'ON_HOLD',
        'FULFILLED',
        'CANCELLED',
        'EXPIRED',
        'MERGED',
    ])
        .default('ALL'),
    /**
     * Case-insensitive substring over id, participant, payee name and account number — or a
     * withdrawer's `account_ref` in full. The ref matches whole, never by substring, for the
     * same reason the history search's ref arm does: it is an opaque customer number pasted in
     * full, and a fragment would match unrelated customers indistinguishably.
     */
    q: z.string().max(120).optional(),
    /**
     * Exact amount as a decimal string — matches the row's current `amount` to the satang, the
     * figure an operator pastes off a site's own ledger line. ANDs with `q` and the tab filters.
     */
    amount: z.string().optional(),
    /** `CREATED` orders by time in the pool, `AMOUNT` by value. */
    sort: z.enum(['CREATED', 'AMOUNT']).default('CREATED'),
    /** Omitted keeps the historical order: WAITING oldest-first, everything else newest-first. */
    dir: z.enum(['ASC', 'DESC']).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
    /**
     * One website's withdrawals, or absent for the whole pool.
     *
     * Distinct from `q`, which already substring-matches the participant among four other
     * columns: a site chosen from a list is an exact predicate the count row agrees with, while
     * typing `web_a` into the search box also matches a payee who happens to contain that text
     * and cannot express "this site and nothing else" at all.
     */
    participant: ParticipantFilterSchema,
})
    .strict();
/** `200` for `GET /ops/v1/withdrawals`. Counts cover the whole pool, items only the page. */
export const WithdrawalQueueResponseSchema = z
    .object({
    items: z.array(WithdrawalQueueRowSchema),
    total: z.number().int().nonnegative(),
    counts: z
        .object({
        waiting: z.number().int().nonnegative(),
        in_flight: z.number().int().nonnegative(),
        done: z.number().int().nonnegative(),
    })
        .strict(),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// POST /ops/v1/withdrawals/{id}/cancel — cancel a waiting withdrawal on the customer's behalf
// ---------------------------------------------------------------------------------------------
/**
 * What is to become of the money once the funds lock is released.
 *
 * `RELEASE` is the cancel this endpoint has always performed: the lock goes back and the amount
 * lands in the customer's website wallet, where they are free to do nothing with it. `MOVE_TO_NORMAL`
 * is for the customer who cannot drive that themselves — support pulls them out of the P2P pool
 * and the website re-files the same amount as an ordinary withdrawal, queued for its own
 * back-office payout, so the customer's money keeps moving without them touching anything.
 *
 * The choice is the *website's* to execute (PF has no ordinary-withdrawal queue of its own); PF's
 * only part is carrying the operator's intent to it on `withdrawal.released`. A website that does
 * not implement the second disposition ignores it and releases, which is why the field is
 * optional here and absent-means-`RELEASE` there.
 */
export const CancelDispositionSchema = z.enum(['RELEASE', 'MOVE_TO_NORMAL']);
/**
 * Request body for `POST /ops/v1/withdrawals/{id}/cancel`.
 *
 * The reason is mandatory — it lands on the `withdrawal.released` event payload, which is the
 * audit trail for this action. Same bounds as the re-match reason.
 *
 * `disposition` is optional and defaults to `RELEASE`, so every caller written before it existed
 * keeps the behaviour it was written against. See {@link CancelDispositionSchema}.
 */
export const OperatorCancelWithdrawalRequestSchema = z
    .object({
    reason: z.string().trim().min(1).max(2000),
    disposition: CancelDispositionSchema.optional(),
})
    .strict();
/** `200` for `POST /ops/v1/withdrawals/{id}/cancel`. Pinned: a successful cancel is CANCELLED. */
export const OperatorCancelWithdrawalResponseSchema = z
    .object({ id: z.string().min(1), status: z.literal('CANCELLED') })
    .strict();
// ---------------------------------------------------------------------------------------------
// POST /ops/v1/transactions/{id}/evidence — file the slip on the customer's behalf
// ---------------------------------------------------------------------------------------------
/**
 * The base64 payload cap: the storage layer's 5 MiB object cap (`S3_MAX_OBJECT_BYTES`) after
 * base64's 4/3 expansion, rounded up to a readable figure. Enforced here so an oversized file is
 * a 400 at the edge, not a storage error mid-flow.
 */
const OPERATOR_SLIP_BASE64_MAX = 7_000_000;
/**
 * Request body for `POST /ops/v1/transactions/{id}/evidence`.
 *
 * The assisted-upload path: when the customer cannot complete the upload themselves, support
 * receives the slip image out-of-band and an operator files it from the console. The file rides
 * the request as base64 — the operator's browser cannot use the participant pre-signed-URL flow,
 * whose SSE-C headers must never reach a browser (see `EvidenceStore.putObject`).
 */
export const OperatorSubmitEvidenceRequestSchema = z
    .object({
    kind: z.literal('BANK_SLIP'),
    /** Pinned to the same three types the participant upload URL accepts. */
    content_type: z.enum(['image/jpeg', 'image/png', 'application/pdf']),
    /** The slip file, standard base64 (no data-URI prefix). */
    file_base64: z
        .string()
        .min(1)
        .max(OPERATOR_SLIP_BASE64_MAX)
        .regex(/^[A-Za-z0-9+/]+={0,2}$/, 'must be standard base64'),
})
    .strict();
/**
 * `202` for `POST /ops/v1/transactions/{id}/evidence` — accepted, exactly like the customer-path
 * `POST /v1/transactions/{id}/evidence`: verification is asynchronous and its verdict arrives on
 * the queue pages, not here.
 */
export const OperatorSubmitEvidenceResponseSchema = z
    .object({
    id: z.string().min(1),
    status: z.literal('VERIFYING'),
    evidence_id: z.string().min(1),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// GET /ops/v1/transactions — finished deposits, for looking back
// ---------------------------------------------------------------------------------------------
/** One finished deposit. Terminal statuses only — in-flight money lives on other screens. */
export const TransactionHistoryRowSchema = z
    .object({
    id: z.string().min(1),
    /** The website that owns this deposit — the *depositor's* side, always present. */
    participant: z.string().min(1),
    /**
     * The website that owns the *matched withdrawal* — the withdrawer's side.
     *
     * `participant` above names the deposit's owner, which is what "this site's history" means
     * (see the read model's scope note) and, since cross-website matching, no longer names the
     * person being paid: a `web_a` deposit can settle a `web_b` withdrawal. Joined off
     * `withdrawal_request` through `matched_withdrawal_id` in the same left join that already
     * supplies `withdrawer_name`, so it is null in exactly the same cases as those fields — an
     * EXPIRED deposit that ended with nothing matched.
     *
     * Optional on the wire — same compatibility reason as the names.
     */
    withdrawal_participant: z.string().min(1).nullable().optional(),
    amount: MoneySchema,
    status: z.enum(['COMPLETED', 'REJECTED', 'EXPIRED']),
    /**
     * How an `EXPIRED` row came to expire — `PARTICIPANT_RELEASE` (the depositor pressed cancel
     * and gave the slot back), `RESERVATION_TTL` (the timer reclaimed it) or `OPERATOR_CANCELLED`
     * (an admin cancelled the matched withdrawal while the reservation was still unpaid; the
     * deposit is expired in the same commit, see `OperatorCancelWithdrawal`). Read off the
     * `transaction.expired` event's `cause`, the same discriminator the abandonment report
     * aggregates. The console renders a release as *cancelled*, which is what it was to the
     * customer — "expired" was only ever the state machine's name for the shared arrow. An
     * operator cancel renders as *cancelled by an admin* for the same reason: the depositor did
     * not run out of time, someone took the slot back. Null on non-EXPIRED rows and on events
     * recorded before the cause existed (those show as expired, the pre-split behaviour);
     * optional on the wire for the usual deploy-order reason.
     */
    expired_cause: z
        .enum(['RESERVATION_TTL', 'PARTICIPANT_RELEASE', 'OPERATOR_CANCELLED'])
        .nullable()
        .optional(),
    matched_withdrawal_id: z.string().nullable(),
    /**
     * The depositor's declared name (verify-the-sender `payer_account`) — null on reservations
     * made without one. Optional on the wire while pre-upgrade APIs still serve this list, so a
     * console deployed ahead of the API renders "—" instead of failing the page.
     */
    depositor_name: z.string().nullable().optional(),
    /** The matched withdrawal's registered payee name. Optional on the wire — same reason. */
    withdrawer_name: z.string().nullable().optional(),
    /**
     * The depositor's `account_ref` — the participating website's own username for the customer,
     * the exact value the `search` param matches whole (and the abandonment report hands off).
     * Qualified `depositor_…` because this row names both sides of the match. Optional on the
     * wire — same compatibility reason as the names.
     */
    depositor_account_ref: z.string().min(1).optional(),
    /**
     * The website's *display* handle for the depositor — see `ReviewCaseContextSchema`'s
     * `account_display` for the full note. Qualified `depositor_…` like the ref. Null until the
     * website sends one; optional on the wire for the compatibility reason.
     */
    depositor_account_display: z.string().min(1).nullable().optional(),
    /**
     * The matched withdrawal's customer, same pair and same rules as the depositor's above.
     * Both null when the deposit ended with nothing matched (an EXPIRED reservation), and the
     * ref is also null on a withdrawal pooled before migration 0020 stored it.
     */
    withdrawer_account_ref: z.string().min(1).nullable().optional(),
    withdrawer_account_display: z.string().min(1).nullable().optional(),
    /**
     * Username of the operator behind the row's most recent human action; null when the whole
     * deposit was automatic. Optional on the wire — same compatibility reason as the names.
     */
    operator_name: z.string().nullable().optional(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
})
    .strict();
/** Query for `GET /ops/v1/transactions`. Offset pagination: history is a rear-view mirror. */
export const TransactionHistoryQuerySchema = z
    .object({
    status: z.enum(['ALL', 'COMPLETED', 'REJECTED', 'EXPIRED']).default('ALL'),
    /**
     * A fragment of a transaction id, *or* a depositor's `account_ref` in full.
     *
     * Two matches behind one param because an operator has one box: ids match by substring, a
     * ref matches only as a whole value. That asymmetry is deliberate — a ref is an opaque
     * customer number minted by the participating website, so a partial one would match
     * unrelated depositors indistinguishably. Refs are unique per participant, not globally, and
     * this endpoint has no tenant filter, so a ref two sites happen to share matches both.
     */
    search: z.string().max(120).optional(),
    /**
     * Exact amount as a decimal string — matches the deposit's amount to the satang. The
     * companion to `search` for the other thing a site's back office hands an operator: not a
     * name but a figure. ANDs with every other filter.
     */
    amount: z.string().optional(),
    /** `FINISHED` orders by the moment the deposit reached its terminal state. */
    sort: z.enum(['FINISHED', 'AMOUNT']).default('FINISHED'),
    dir: z.enum(['ASC', 'DESC']).default('DESC'),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
    /**
     * One website's deposits, or absent for the whole pool.
     *
     * This is also what resolves the ambiguity `search` documents above: an `account_ref` is
     * unique per participant, so a ref two sites both minted matches both rows — until the
     * caller names the site, at which point it matches one.
     */
    participant: ParticipantFilterSchema,
})
    .strict();
/** `200` for `GET /ops/v1/transactions`. */
export const TransactionHistoryResponseSchema = z
    .object({
    items: z.array(TransactionHistoryRowSchema),
    total: z.number().int().nonnegative(),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// GET /ops/v1/lookup — one box that answers "where is this customer's money right now"
// ---------------------------------------------------------------------------------------------
/**
 * What the lookup decided the query *is*, echoed back so the console can label the result page
 * ("ผลการค้นหาด้วยยอดเงิน") and so an operator can see when their paste was read as something
 * other than they meant.
 *
 * Detection is by shape, server-side, in this order: a `txn_…` id, a `wd_…` id, a lock
 * reference (`lk_…` or anything carrying a `#<n>` slice suffix), a plain decimal amount, and
 * otherwise an account — a username / `account_ref`, matched whole on both the depositor and
 * withdrawer sides.
 */
export const OPS_LOOKUP_KINDS = [
    'TRANSACTION_ID',
    'WITHDRAWAL_ID',
    'LOCK_REFERENCE',
    'AMOUNT',
    'ACCOUNT',
];
/** Query for `GET /ops/v1/lookup`. One box, no tabs — the server works out what `q` is. */
export const OpsLookupQuerySchema = z
    .object({
    /** The pasted thing: an id, a lock reference, an amount, or a username. */
    q: z.string().trim().min(1).max(120),
    /** Per-bucket cap — each of the three result groups is capped independently. */
    limit: z.coerce.number().int().min(1).max(100).default(25),
    /** One website's rows, or absent for every site's. See {@link ParticipantFilterSchema}. */
    participant: ParticipantFilterSchema,
})
    .strict();
/**
 * A deposit as the lookup sees it — the history row, with `status` widened to the full deposit
 * lifecycle. `GET /ops/v1/transactions` deliberately serves terminal rows only ("history is a
 * rear-view mirror"), but the lookup's whole point is the opposite question: *where is this
 * money right now*, which is most valuable exactly when the answer is RESERVED or VERIFYING.
 * `expired_cause` keeps its meaning and is simply null anywhere pre-terminal.
 */
export const OpsLookupTransactionRowSchema = TransactionHistoryRowSchema.extend({
    status: DepositStatusSchema,
}).strict();
/**
 * `200` for `GET /ops/v1/lookup`.
 *
 * Three buckets, always all present (empty arrays, never absent): deposits across their whole
 * lifecycle, withdrawal-queue rows, and review cases — the three tables a customer's money can
 * be sitting in. Each bucket's `total` counts every match while `items` is capped by `limit`,
 * so the console can say "แสดง 25 จาก 329" instead of silently truncating.
 *
 * Buckets reuse the list-screen row schemas verbatim (the withdrawal and review rows are those
 * screens' own shapes) so a row here renders with the same components and deep-links to the
 * same detail pages the operator already knows.
 */
export const OpsLookupResponseSchema = z
    .object({
    query_kind: z.enum(OPS_LOOKUP_KINDS),
    transactions: z
        .object({
        items: z.array(OpsLookupTransactionRowSchema),
        total: z.number().int().nonnegative(),
    })
        .strict(),
    withdrawals: z
        .object({
        items: z.array(WithdrawalQueueRowSchema),
        total: z.number().int().nonnegative(),
    })
        .strict(),
    review_cases: z
        .object({
        items: z.array(ReviewQueueRowSchema),
        total: z.number().int().nonnegative(),
    })
        .strict(),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// GET /ops/v1/metrics/amount-distribution — where the settled money actually sits
// ---------------------------------------------------------------------------------------------
/**
 * Histogram bucket edges, in major units of the ledger currency. The top bucket is open-ended.
 *
 * Deliberately uneven, and that is the whole design. Transfer amounts are a right-skewed
 * distribution — a long tail of large deposits over a dense mass of small ones — so equal-width
 * buckets are useless in both directions at once: a 1 000-wide bucket puts nearly every deposit in
 * bucket one and shows a single bar, and a bucket narrow enough to resolve that mass would need
 * hundreds of them to reach the tail. These edges are instead semi-log — each step is roughly
 * 2–3× the last — and then snapped to round numbers people actually transfer, so an operator reads
 * a bar as "the 500–1 000 band" rather than as "the 512–1 024 band".
 *
 * The buckets are half-open, `[lower, upper)`: an amount exactly on an edge belongs to the bucket
 * *above* it. That is the only convention under which "500–1 000" and "1 000–2 000" mean what an
 * operator assumes they mean, and it keeps every amount in exactly one bucket.
 *
 * Eight edges therefore describe eight buckets, the last of which has no upper bound — the tail
 * has no natural ceiling, and closing it would silently drop the outliers that are the most
 * interesting rows on the chart.
 */
export const AMOUNT_BUCKET_EDGES = [0, 100, 300, 500, 1000, 2000, 5000, 10000];
/**
 * One bar of the histogram.
 *
 * Both bounds and both money figures are decimal *strings*, like every other amount on this wire:
 * they come out of `NUMERIC`/`DECIMAL` columns and must never round-trip through a JS `number`.
 */
export const AmountBucketSchema = z
    .object({
    /** Inclusive lower bound, decimal string. */
    lower: z.string(),
    /** Exclusive upper bound; `null` marks the open-ended top bucket. */
    upper: z.string().nullable(),
    count: z.number().int().nonnegative(),
    /** Summed amount of the bucket, decimal string. */
    volume: z.string(),
})
    .strict();
/**
 * `200` response for `GET /ops/v1/metrics/amount-distribution`.
 *
 * Answers "where do successful transaction amounts cluster?" over the same window the KPI
 * dashboard uses — the request carries {@link DashboardMetricsQuerySchema}, so `window_hours` and
 * an absolute `from`/`to` range mean exactly what they mean on `GET /ops/v1/metrics`.
 *
 * `buckets` always carries one entry per band described by {@link AMOUNT_BUCKET_EDGES}, including
 * the empty ones, so the chart has a stable shape and a quiet window renders as flat bars rather
 * than as a different-looking chart. `median` and `p90` are `"0"` on an empty window for the same
 * reason.
 *
 * The pair of percentiles is here because the buckets alone cannot answer "is the middle of this
 * distribution moving?" — a shift within one band is invisible on the bars — while the bars answer
 * the shape question the percentiles cannot.
 */
export const AmountDistributionSchema = z
    .object({
    /** Inclusive start of the window the histogram covers. */
    range_start: z.string().datetime({ offset: true }),
    /** Exclusive end of the window the histogram covers. */
    range_end: z.string().datetime({ offset: true }),
    /** When the projection was computed — not `range_end` when the window already closed. */
    generated_at: z.string().datetime({ offset: true }),
    currency: z.string(),
    buckets: z.array(AmountBucketSchema),
    total_count: z.number().int().nonnegative(),
    /** Summed amount across every bucket, decimal string. */
    total_volume: z.string(),
    /** Nearest-rank 50th percentile of the window's amounts, decimal string. */
    median: z.string(),
    /** Nearest-rank 90th percentile of the window's amounts, decimal string. */
    p90: z.string(),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// GET /ops/v1/metrics/amount-scatter — the same amounts, against the clock
// ---------------------------------------------------------------------------------------------
/**
 * How many individual deposits the scatter endpoint will ever return.
 *
 * A scatter plot is the one projection on this dashboard whose payload grows with the *book*
 * rather than with the window: a busy month is hundreds of thousands of settled deposits, and
 * nothing good happens when a browser is handed that many marks to draw. So the endpoint takes a
 * systematic sample and says so — {@link AmountScatterSchema}'s `total_count` is always the true
 * population, and `sampled` tells the console whether what it is drawing is all of it.
 *
 * 2 000 is chosen against the plot, not against the wire. At the widths this chart is drawn at,
 * that is already far past the point where added marks change the *shape* an operator reads — the
 * dense band and the outliers above it are both fully resolved — while staying inside what an SVG
 * of individual circles renders without stutter. Raising it would cost frames and buy nothing;
 * lowering it starts to thin the tail, which is where the interesting rows are.
 */
export const AMOUNT_SCATTER_SAMPLE_LIMIT = 2000;
/**
 * One settled deposit, as a mark on the scatter.
 *
 * Deliberately not a `Money`: this is a plot point and there are up to
 * {@link AMOUNT_SCATTER_SAMPLE_LIMIT} of them, so repeating `"currency": "THB"` two thousand times
 * would be the payload's largest single component and would say nothing the envelope's own
 * `currency` does not already say once.
 */
export const AmountScatterPointSchema = z
    .object({
    /** When the deposit was created — the x position. */
    at: z.string().datetime({ offset: true }),
    /** The deposit's amount, decimal string, exactly as every other amount on this wire. */
    amount: z.string(),
})
    .strict();
/**
 * `200` response for `GET /ops/v1/metrics/amount-scatter`.
 *
 * The sibling of `GET /ops/v1/metrics/amount-distribution`, over the identical window contract —
 * the request carries {@link DashboardMetricsQuerySchema}, and both endpoints resolve it by the
 * same rules, so the two panels can never end up describing different spans.
 *
 * ## Why a second projection of the same rows
 *
 * The histogram collapses time. It answers "which amounts?" perfectly and "when?" not at all, so
 * a window in which every large deposit arrived in one ten-minute burst and a window in which they
 * trickled in evenly produce the *same bars*. Those are very different facts about a payment book:
 * one is a batch, a payroll run or a promotion; the other is normal trade. This projection keeps
 * both dimensions and lets an operator see the burst, the quiet hour, and the outlier that sits
 * alone at the top — none of which survive bucketing.
 *
 * ## The sample is part of the contract
 *
 * `points` is capped at {@link AMOUNT_SCATTER_SAMPLE_LIMIT}. `total_count` is the *unsampled*
 * population, and `sampled` is true exactly when the two disagree, so a console can say "2,000 of
 * 48,913" rather than silently presenting a sample as a census. The sample is systematic — every
 * k-th deposit in time order — which preserves relative density: an hour with ten times the
 * deposits of another still shows ten times the marks.
 */
export const AmountScatterSchema = z
    .object({
    /** Inclusive start of the window the marks cover — also the x axis' left edge. */
    range_start: z.string().datetime({ offset: true }),
    /** Exclusive end of the window — the x axis' right edge. */
    range_end: z.string().datetime({ offset: true }),
    /** When the projection was computed — not `range_end` when the window already closed. */
    generated_at: z.string().datetime({ offset: true }),
    currency: z.string(),
    /** Up to {@link AMOUNT_SCATTER_SAMPLE_LIMIT} deposits, ordered by `at`. */
    points: z.array(AmountScatterPointSchema),
    /** Settled deposits in the window *before* sampling. Never smaller than `points.length`. */
    total_count: z.number().int().nonnegative(),
    /** True when `points` is a sample rather than the whole window. */
    sampled: z.boolean(),
    /** The cap that was applied, echoed so the console never hardcodes it. */
    sample_limit: z.number().int().positive(),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// GET /ops/v1/depositors/abandonment — who reserves and then doesn't follow through
// ---------------------------------------------------------------------------------------------
/**
 * The reservation-abandonment report (SAD §5 abuse controls, made visible).
 *
 * `depositor_reputation` has counted reservation abuse since migration 0008 and no operator has
 * ever been able to see it: nothing in this package read a single one of its columns. This section
 * is that surface. It answers one question — *who takes a slot out of the pool and does not use
 * it* — and separates the two ways that happens, because they are not the same behaviour and do
 * not deserve the same response:
 *
 *  - **cancelled** — the slot was released deliberately. Wasteful, but cooperative: the pool got
 *    it back early.
 *  - **timed out** — the slot was held until the reservation TTL reclaimed it. Strictly worse. For
 *    the entire window a withdrawal sat unmatched that could have been matched to someone else.
 *
 * **Deliberately not money-weighted.** There is no amount, no volume and no value anywhere in
 * these schemas, and that is a product decision rather than an oversight: a held slot costs the
 * pool the same whatever amount is attached to it, and ranking by value would turn an abuse report
 * into a VIP report. Counts and rates only.
 */
/** How the abandonment list is ranked. See {@link DepositorAbandonmentRowSchema.abandon_score}. */
export const DEPOSITOR_ABANDONMENT_SORTS = [
    'ABANDON_SCORE',
    'ABANDON_RATE',
    'TIMEOUT_RATE',
    'RESERVATIONS',
    'TIMED_OUT',
    'CANCELLED',
    'LAST_SEEN',
];
/** One depositor's behaviour over the window. */
export const DepositorAbandonmentRowSchema = z
    .object({
    /**
     * The website the reservations were made through — *not* the person. A depositor's identity is
     * the pair (`participant`, `account_ref`), which is the composite key `depositor_reputation`
     * itself uses; the same `account_ref` under two participants is two different people.
     */
    participant: z.string().min(1),
    /** The participant's own opaque reference for the depositor. The other half of the identity. */
    account_ref: z.string().min(1),
    /**
     * The website's *display* handle for the depositor — see `ReviewCaseContextSchema`'s
     * `account_display` for the full note. Display-only: the identity this report aggregates by
     * stays the pair above. Null until the website sends one; optional on the wire while
     * pre-upgrade APIs still serve the report.
     */
    account_display: z.string().min(1).nullable().optional(),
    /** Reservations opened inside the window. The denominator of every rate below. */
    reservations_total: z.number().int().nonnegative(),
    /** Deposits that reached COMPLETED inside the window. */
    completed: z.number().int().nonnegative(),
    /** Slots the participant released deliberately. */
    cancelled: z.number().int().nonnegative(),
    /** Slots the reservation TTL reclaimed — held for the full window. The expensive behaviour. */
    timed_out: z.number().int().nonnegative(),
    /** Duplicate-slip and not-a-slip rejections inside the window (SAD §6 fraud class). */
    fraud_rejects: z.number().int().nonnegative(),
    /**
     * `(cancelled + timed_out) / reservations_total`, in `[0, 1]`. 0 when there were no
     * reservations.
     *
     * Reported *beside* {@link DepositorAbandonmentRowSchema.abandon_score} rather than instead of
     * it: this is the number an operator quotes when they act on a row, but it is a terrible
     * ranking key — see the score's own note.
     */
    abandon_rate: z.number().min(0).max(1),
    /** `timed_out / reservations_total`, in `[0, 1]` — the worse half on its own. */
    timeout_rate: z.number().min(0).max(1),
    /**
     * The 95 % **Wilson score lower bound** on `abandon_rate`, in `[0, 1]`. The default ranking.
     *
     * Sorting by the raw rate puts a depositor with one reservation and no deposit — 100 % — above
     * a depositor with 200 reservations and 120 abandonments — 60 % — which buries the only row
     * worth acting on. The Wilson bound ranks by the pessimistic end of a confidence interval
     * around the rate instead, so a rate measured over few trials is discounted in proportion to
     * how little is known about it, and converges on the raw rate as the sample grows. Those two
     * examples score ≈ 0.21 and ≈ 0.53 respectively (the small sample is discounted far harder). The formula and the zero-reservation case are
     * documented on `wilsonLowerBound` in `@pf/persistence`.
     *
     * Always ≤ `abandon_rate`. A UI should show the rate and sort by the score.
     */
    abandon_score: z.number().min(0).max(1),
    /**
     * Current abuse tier from `depositor_reputation` — *current state*, not window history.
     * `STANDARD` also means "no reputation row exists yet", i.e. never penalized.
     */
    tier: z.enum(['STANDARD', 'RESTRICTED', 'BLOCKED']),
    /** Current cooldown expiry, or null when not cooling down. Current state, like `tier`. */
    cooldown_until: z.string().datetime({ offset: true }).nullable(),
    /** Current rung of the escalating cooldown ladder; 0 when never penalized. */
    cooldown_level: z.number().int().nonnegative(),
    /** The depositor's most recent event **inside the window** — not their all-time last activity. */
    last_seen_at: z.string().datetime({ offset: true }),
})
    .strict();
/**
 * Query for `GET /ops/v1/depositors/abandonment`.
 *
 * The window half is `window_hours` XOR `from`/`to`, with the identical bounds, the identical
 * exclusivity rule and the identical span limits as {@link DashboardMetricsQuerySchema} — the
 * shape is restated rather than reused because that schema is a `ZodEffects` (it carries a
 * `superRefine`) and Zod cannot `.extend()` one. The rules are shared through
 * {@link METRICS_MIN_WINDOW_HOURS}/{@link METRICS_MAX_WINDOW_HOURS} and, on the server, through the
 * single `resolveRange` helper both endpoints call — so the two can drift in wording here but
 * never in behaviour.
 *
 * Offset pagination, like the other operator lists: this is a report an operator reads and sorts,
 * not a live feed, and a cursor would make "jump to page 4 by timeout count" impossible.
 */
export const DepositorAbandonmentQuerySchema = z
    .object({
    /** Trailing window ending now. Mutually exclusive with `from`/`to`. */
    window_hours: z.coerce.number().int().positive().max(METRICS_MAX_WINDOW_HOURS).optional(),
    /** Inclusive start of an absolute range, ISO-8601 with offset. */
    from: z.string().datetime({ offset: true }).optional(),
    /** Exclusive end of an absolute range, ISO-8601 with offset. Defaults to now. */
    to: z.string().datetime({ offset: true }).optional(),
    /**
     * Drop depositors with fewer than this many reservations in the window.
     *
     * Defaults to 1, and that default is load-bearing rather than cosmetic: at 0 the list includes
     * depositors whose reservation opened *before* the window and expired *inside* it, whose rates
     * are all 0/0. They are real and occasionally worth seeing, but they are not what the report
     * is for, and they would otherwise pad every first page. Raise it to ask the sharper question
     * ("of the people who reserve regularly, who is wasting slots?").
     */
    min_reservations: z.coerce.number().int().min(0).max(10_000).default(1),
    sort: z.enum(DEPOSITOR_ABANDONMENT_SORTS).default('ABANDON_SCORE'),
    /** Worst-first by default: every sort key here is one where "more" means "more concerning". */
    dir: z.enum(['ASC', 'DESC']).default('DESC'),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
    /**
     * One website's depositors, or absent for every site's.
     *
     * The report already groups by participant — a depositor is `(participant, account_ref)`, not
     * a global person — so this narrows which groups are counted rather than changing how they
     * are formed, and `min_reservations` keeps meaning exactly what it meant.
     */
    participant: ParticipantFilterSchema,
})
    .strict()
    .superRefine((query, ctx) => {
    const hasRange = query.from !== undefined || query.to !== undefined;
    if (query.window_hours !== undefined && hasRange) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['window_hours'],
            message: 'window_hours cannot be combined with from/to',
        });
        return;
    }
    if (query.to !== undefined && query.from === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['from'],
            message: 'from is required when to is given',
        });
        return;
    }
    if (query.from === undefined)
        return;
    // `fromIso`, not `new Date`/`Date.parse`: R5 bans the `Date` global outside `@pf/shared`'s
    // clock module, and it cannot tell parsing a caller's string from reading the wall clock.
    const from = fromIso(query.from).getTime();
    // `to` absent means "until now"; only the server knows how long that is, so the span bounds
    // below are applied there too — see `resolveRange`.
    if (query.to === undefined)
        return;
    const to = fromIso(query.to).getTime();
    if (to <= from) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: 'to must be after from' });
        return;
    }
    const hours = (to - from) / 3_600_000;
    if (hours < METRICS_MIN_WINDOW_HOURS) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['to'],
            message: `range must span at least ${String(METRICS_MIN_WINDOW_HOURS)} hour`,
        });
    }
    if (hours > METRICS_MAX_WINDOW_HOURS) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['to'],
            message: `range must span at most ${String(METRICS_MAX_WINDOW_HOURS)} hours`,
        });
    }
});
/**
 * `200` for `GET /ops/v1/depositors/abandonment`.
 *
 * `total` counts depositors matching `min_reservations` across the whole window, beyond the page —
 * the same contract as the other operator lists. The window bounds are echoed off the projection
 * rather than off the request, so the screen always states the range the numbers were actually
 * computed over; `generated_at` is "as of", which is not `range_end` once the window has closed.
 */
export const DepositorAbandonmentResponseSchema = z
    .object({
    items: z.array(DepositorAbandonmentRowSchema),
    total: z.number().int().nonnegative(),
    range_start: z.string().datetime({ offset: true }),
    range_end: z.string().datetime({ offset: true }),
    /**
     * The earliest instant from which the `cancelled` / `timed_out` split can be trusted — or null
     * when it cannot be trusted over any window yet.
     *
     * **A consumer must compare this against `range_start` and warn when the window starts
     * earlier.** Until the deposit machine learned to distinguish a deliberate release from a TTL
     * sweep, both were recorded as `transaction.expired` with a hard-coded cause of
     * `RESERVATION_TTL`. Those rows are indistinguishable and cannot be back-filled — the two
     * paths were byte-identical in the event store — so a window reaching back past this instant
     * reports releases *as timeouts*.
     *
     * The error is not symmetric and that is why it is worth a banner rather than a footnote: it
     * inflates `timed_out`, which is the more serious of the two behaviours and the one an
     * operator escalates on, so a pre-boundary window makes depositors look worse than they were.
     * `reservations_total`, `completed`, `fraud_rejects`, `abandon_rate` and `abandon_score` are
     * unaffected — only `timed_out`, `cancelled` and `timeout_rate` are.
     */
    split_reliable_from: z.string().datetime({ offset: true }).nullable(),
    generated_at: z.string().datetime({ offset: true }),
})
    .strict();
/**
 * How healthy one provider's remaining quota is, as the dashboard should colour it.
 *
 * `UNAVAILABLE` is not a quota level — it means the question could not be answered (the provider's
 * own account endpoint timed out, refused the key, or the provider is configured without one).
 * It is kept in the same enum rather than modelled as a null status so the console cannot forget
 * to render the case where PaymentFlow does not know how much fallback it has left.
 */
export const ProviderQuotaStatusSchema = z.enum(['OK', 'LOW', 'EXHAUSTED', 'UNAVAILABLE']);
/**
 * One slip-verification provider's quota, normalised to **whole slips**.
 *
 * Normalising the unit is the entire point of this schema, and it is a money-safety concern rather
 * than a cosmetic one. The vendors do not agree on what they are counting: Thunder meters in slips
 * (`used`/`max` are slip counts), while Slip2Go meters in *tokens* and charges a fraction of a
 * token per slip (0.25 at the BASIC tier). Reporting each vendor's raw number side by side would
 * put "4963" next to "87" on the same row and invite the reader to conclude the fallback is nearly
 * dry when it holds ~348 slips — or, at a different tier, the reverse. `slips_*` is therefore the
 * only pair the UI should compare or threshold on.
 *
 * `native_*` exists so the operator can still reconcile the screen against the vendor's own portal,
 * which shows the raw meter. Without it, "the console says 348 but Slip2Go says 87" reads as a bug
 * in PaymentFlow and costs someone an afternoon.
 */
export const ProviderQuotaSchema = z
    .object({
    /** Provider name as it appears in `SLIP_PROVIDERS` and on the attestation, e.g. `thunder`. */
    provider: z.string().min(1),
    /**
     * 1-based position in the live verification chain; `null` when the provider is reachable and
     * credentialed but absent from `SLIP_PROVIDERS`.
     *
     * The dashboard needs this to say *which* provider is the one currently spending quota: a
     * primary at 12% and a dormant fallback at 12% are very different afternoons.
     */
    position: z.number().int().positive().nullable(),
    status: ProviderQuotaStatusSchema,
    /** Slips consumed in the current billing period. `null` when `status` is `UNAVAILABLE`. */
    slips_used: z.number().int().nonnegative().nullable(),
    /** Slips left in the current billing period. `null` when `status` is `UNAVAILABLE`. */
    slips_remaining: z.number().int().nonnegative().nullable(),
    /** Slips the period started with. `null` when `status` is `UNAVAILABLE`. */
    slips_limit: z.number().int().nonnegative().nullable(),
    /** What the vendor itself meters in — `slip`, `token`, … — for reconciliation only. */
    native_unit: z.string().nullable(),
    /** The vendor's own remaining figure, as a decimal string so a fractional meter survives. */
    native_remaining: z.string().nullable(),
    /** The vendor's plan name, e.g. `EXPERT`, `BASIC-1`. */
    package_name: z.string().nullable(),
    /** When the current package lapses — a quota that renews tomorrow is not a quota that is fine. */
    expires_at: z.string().datetime({ offset: true }).nullable(),
    /** Spare credit beyond the package, as a decimal string. */
    credit: z.string().nullable(),
    /**
     * Whether the vendor will actually spend `credit` on a slip check.
     *
     * Sent because credit on its own does not mean the vendor still works: Slip2Go spends it on
     * slip checks only when the shop has `checkSlipByCredit` set, so a package at zero with credit
     * in hand and that switch off is exhausted in every sense that matters. The console reads the
     * two together, and `status` is computed from them together. `null` for a vendor with no such
     * concept, and when `status` is `UNAVAILABLE`.
     */
    credit_usable: z.boolean().nullable(),
    /** When this figure was actually read from the provider — it is cached, so it is not "now". */
    checked_at: z.string().datetime({ offset: true }),
    /**
     * Why the figure is missing, when `status` is `UNAVAILABLE` — `TIMEOUT`, `UNAUTHORIZED`,
     * `NOT_CONFIGURED`, … Never carries the provider's response body, and never the credential.
     */
    error_code: z.string().nullable(),
})
    .strict();
/**
 * `200` for `GET /ops/v1/provider-quota`.
 *
 * Platform-wide by construction: PaymentFlow holds one account per vendor, so there is nothing to
 * partition by participant and the endpoint takes no `participant` filter. The console must not
 * send one — see the note on the web-scope notice in the dashboard page.
 *
 * Always `200` with a populated `providers` array once at least one provider is configured: a
 * provider PaymentFlow cannot reach is reported as an `UNAVAILABLE` row, never as a failed request,
 * so one sick vendor cannot blank the panel and hide the health of the other.
 */
export const ProviderQuotaResponseSchema = z
    .object({
    providers: z.array(ProviderQuotaSchema),
    generated_at: z.string().datetime({ offset: true }),
})
    .strict();
/**
 * One deposit that was matched against a slice, whether or not it ended in payment.
 *
 * A slice accumulates these: it may be reserved, fail, return to the pool, and be claimed again,
 * and each of those is a separate `transaction` row. The operator screen must show all of them —
 * the withdrawal queue's own read model keeps only the newest match per withdrawal
 * (`ROW_NUMBER() … rn = 1`), which is the right answer for a queue and the wrong answer here,
 * because the earlier rows *are* the failure history someone opened this view to read.
 *
 * The link is drawn from `transaction.matched_withdrawal_id`, never from
 * `withdrawal_request.reserved_by`: the latter is set to NULL every time a slice returns to the
 * pool, so a view built on it would go blank for exactly the failed attempts it is meant to show.
 */
export const WithdrawalSliceAttemptSchema = z
    .object({
    /** The deposit's id — links to `GET /ops/v1/transactions/{id}`, which already exists. */
    transaction_id: TransactionIdSchema,
    status: DepositStatusSchema,
    amount: MoneySchema,
    /**
     * The bank's own reference for the slip, once a provider attested one. `null` while no slip
     * has been verified — an evidence row exists before an image does.
     */
    trans_ref: z.string().min(1).nullable(),
    /**
     * When the slip was attested. This, not the presence of a stored result, is what "verified"
     * means: a provider *refusal* is recorded into the same column while this stays null.
     */
    verified_at: z.string().datetime({ offset: true }).nullable(),
    /** The manual-review case this deposit raised, when it raised one. */
    review_case_id: ReviewCaseIdSchema.nullable(),
    /**
     * Whether a raw uploaded file exists for this deposit — i.e. whether
     * `GET /ops/v1/transactions/{id}/evidence-file` would hand back bytes rather than 404.
     *
     * Sent so the lineage can offer "view slip" only where there is something to view. It is not
     * implied by `trans_ref`: a QR-only submission is attested without a photograph ever being
     * uploaded, and an unverified attempt can carry an image while `trans_ref` is still null. The
     * one case this can overstate is an object the store has since lost, which the viewer reports
     * when it opens.
     */
    has_slip_file: z.boolean(),
    created_at: z.string().datetime({ offset: true }),
})
    .strict();
/**
 * One occasion on which a slice went back on the book, and why.
 *
 * **This is the only record that a slice ever failed.** A failed slice does not reach a failed
 * state — it returns to `AVAILABLE`, indistinguishable by status from one that has never been
 * touched. Rendering `status` alone would report a slice that has been rejected three times as
 * simply available, so a lineage view that omits this is not a shorter answer, it is a wrong one.
 *
 * These come from the event store (`withdrawal.repooled`). That event is deliberately not in the
 * webhook catalogue, so no downstream consumer can see it and the operator console is the only
 * surface where this history is visible at all.
 */
export const WithdrawalSliceRepoolSchema = z
    .object({
    /** `RESERVATION_TTL`, `DEPOSIT_REJECTED`, `OPERATOR_NOT_PAID`, … */
    cause: z.string().min(1),
    at: z.string().datetime({ offset: true }),
})
    .strict();
/**
 * The moment a withdrawal's funds lock was released back to the withdrawer, and the reason.
 *
 * Deliberately not called a refund. PaymentFlow does not hold the withdrawer's balance — the
 * participating website does — so the honest claim is "the lock was released at this instant with
 * this cause", which is a fact PaymentFlow owns. "฿X was returned" would be a number PaymentFlow
 * cannot vouch for, and it stops being true the moment the released fragment is matched again.
 */
export const WithdrawalReleaseSchema = z
    .object({
    /** `WITHDRAWER_CANCELLED`, `OPERATOR_CANCELLED`, `MAX_AGE`. */
    cause: z.string().min(1),
    at: z.string().datetime({ offset: true }),
})
    .strict();
/**
 * One node of a split-fill lineage — the shrunk parent, or one slice cut from it.
 *
 * Parent and slice share a shape because they are the same kind of row. Under the shrink-the-
 * parent design the parent is not a container that was replaced by children: it is still the
 * original withdrawal, still matchable, now worth the remainder, keeping its own id, its own
 * `created_at` and its own `lock_reference`. An operator reading this view has to be able to see
 * that the parent is simultaneously "the leftover" and "the original request", so flattening it
 * into a summary header would hide the one thing that is confusing about it.
 */
export const WithdrawalLineageNodeSchema = z
    .object({
    id: z.string().min(1),
    /** `<parent-ref>` on the parent, `<parent-ref>#<n>` on a slice. */
    lock_reference: z.string().min(1),
    /** What this row is worth now. On the parent this is the residual, not the original request. */
    amount: MoneySchema,
    /** What this row was worth at creation. Written once, never rewritten — the receipt. */
    origin_amount: MoneySchema,
    status: z.enum([
        'AVAILABLE',
        'RESERVED',
        'LOCKED',
        'ON_HOLD',
        'FULFILLED',
        'CANCELLED',
        'EXPIRED',
        /** Folded back into the origin (0047). Slices only, and never a funds-lock release. */
        'MERGED',
    ]),
    cancelled_by: z.enum(['WITHDRAWER', 'OPERATOR']).nullable(),
    /** How many slices have been cut from this row. Non-zero only on a parent. */
    split_count: z.number().int().nonnegative(),
    /** 1-based position among its parent's slices; `null` on the parent itself. */
    split_sequence: z.number().int().min(1).nullable(),
    /** Every deposit matched against this row, oldest first. */
    attempts: z.array(WithdrawalSliceAttemptSchema),
    /** Every time this row went back on the book, oldest first — the failure history. */
    repools: z.array(WithdrawalSliceRepoolSchema),
    /** Set once the funds lock was released; `null` while the withdrawer's balance is still held. */
    released: WithdrawalReleaseSchema.nullable(),
    /**
     * Set once this slice folded back into its origin (0047); `null` otherwise. `cause` is which
     * of the three re-pool triggers preceded the fold.
     *
     * Mutually exclusive with `released` — a slice ends one way or the other — but a separate
     * field rather than a variant of it, because they mean opposite things about the withdrawer's
     * money: `released` says it went back to their wallet, this says it went back on the book
     * inside the row above.
     */
    merged: WithdrawalReleaseSchema.nullable(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
    /** When the row reached FULFILLED, CANCELLED, EXPIRED or MERGED. */
    terminated_at: z.string().datetime({ offset: true }).nullable(),
})
    .strict();
/** The arithmetic an operator would otherwise do by hand, plus the check that it adds up. */
export const WithdrawalLineageTotalsSchema = z
    .object({
    /** The parent's `origin_amount` — what the withdrawer asked for. */
    origin_amount: MoneySchema,
    /** The parent's current `amount` — what is still on the book under the original request. */
    residual_amount: MoneySchema,
    /** The sum of every slice's `amount`, in every state. */
    sliced_amount: MoneySchema,
    /**
     * Whether `origin_amount === residual_amount + sliced_amount`, compared as decimal strings.
     *
     * The invariant holds by construction — the shrink and the insert are one transaction, and the
     * split `UPDATE` is the only statement in the system permitted to write `amount`. So `false`
     * here is not a rendering artefact to be tolerated; it means a row was written by something
     * that had no business writing it, and it deserves to be shown as an alarm.
     */
    reconciles: z.boolean(),
    /** How many slices were cut. */
    slices_total: z.number().int().nonnegative(),
    /** Slices that reached FULFILLED — the withdrawer was paid these. */
    fulfilled: z.number().int().nonnegative(),
    /** Slices still working: AVAILABLE, RESERVED, LOCKED or ON_HOLD. */
    in_flight: z.number().int().nonnegative(),
    /** Slices whose funds lock was released back — CANCELLED or EXPIRED. */
    released: z.number().int().nonnegative(),
    /**
     * Slices that folded back into the origin — MERGED (0047).
     *
     * Neither paid nor released: the amount returned to the row above and is counted there, in
     * `residual_amount`, which is why these are excluded from `sliced_amount` and why
     * `reconciles` stays true across a fold. Reads as "this many attempts left no trace".
     */
    merged: z.number().int().nonnegative(),
    /**
     * How many times any slice went back on the book.
     *
     * Counted separately from `released` on purpose: a re-pool is a failed *attempt*, not a
     * terminated slice, and a slice can rack up several before it succeeds. Folding the two
     * together would let a slice that failed twice and then paid read as a loss.
     */
    repool_events: z.number().int().nonnegative(),
})
    .strict();
/**
 * `200` for `GET /ops/v1/withdrawals/{id}/lineage`.
 *
 * Answers, for one withdrawal: how many slices were cut and for how much, which deposit and which
 * slip filled each, how many were paid, which failed and why, and when a funds lock went back to
 * the withdrawer.
 *
 * `id` may be the parent's or any slice's — the route resolves to the root either way, because an
 * operator arriving from a deposit holds a slice id and should not have to know that.
 *
 * Bounded by the `split_max_slices` policy, so there is no pagination: a lineage is a handful of
 * rows by construction, and paginating it would let a screen show half an answer to a question
 * that is only meaningful whole.
 */
export const WithdrawalLineageResponseSchema = z
    .object({
    origin: WithdrawalLineageNodeSchema,
    slices: z.array(WithdrawalLineageNodeSchema),
    totals: WithdrawalLineageTotalsSchema,
    generated_at: z.string().datetime({ offset: true }),
})
    .strict();
/**
 * What a shadow reviewer concluded about a case.
 *
 * The first four mirror {@link ReviewCaseDecisionSchema} exactly, minus `REVERIFY` — which is not
 * a verdict about the money but a decision to re-ask the provider, and is excluded from the
 * operator's own proposal schema for the same reason.
 *
 * `ABSTAIN` has no operator counterpart and is the most important value here. Several review
 * reasons are decided on evidence that is not in the case record at all: `SLIP_ALREADY_CLAIMED`
 * turns on whether *another* transaction was already credited, `INCOMPLETE_DATA` is decided by
 * looking at the uploaded image, `EVIDENCE_DEADLINE_BREACHED` by opening a bank portal. A reviewer
 * that answers those confidently is not doing well, it is guessing; one that abstains is telling
 * the truth. Knowing when it cannot decide is the property that matters before anyone considers
 * letting it decide anything, so it is a first-class outcome rather than a missing answer.
 */
export const ShadowVerdictSchema = z.enum([
    'APPROVE',
    'REJECT',
    'EXTERNAL',
    'APPROVE_ADJUSTED',
    'ABSTAIN',
]);
/**
 * How much a comparison on this case is worth — a property of the *review reason*, fixed in
 * advance, never inferred from the outcome.
 *
 * Publishing one agreement percentage across all reasons would be the most misleading number this
 * system could produce. The reasons are different jobs: judging whether "น.ส. สมหญิง ก" and
 * "สมหญิง กมลวรรณ" are one person is a language question an LLM may genuinely be good at, while
 * deciding whether a duplicate reference was already paid for requires a record the reviewer was
 * never shown. Averaging the two hides both answers.
 *
 * - `SCORABLE` — everything the human weighed is in the case record. Agreement here means
 *   something. `NAME_BAND` is the clearest example.
 * - `UNDERDETERMINED` — decidable in principle, but with a known confound or a partial view.
 *   `AMOUNT_MISMATCH` is one: whether `adjustment_preview` is non-null nearly determines
 *   `APPROVE_ADJUSTED`, so a reviewer can score well by learning "if the button was offered, press
 *   it". `ATTEMPTS_EXHAUSTED` is another: the human weighs every attempt, the reviewer is shown
 *   only the latest attested one — for the reason whose name is "there were three attempts".
 * - `NOT_DECIDABLE` — the deciding evidence is outside the case record. `ABSTAIN` is the correct
 *   answer; anything else is a guess, and agreement is coincidence.
 */
export const ShadowFairnessSchema = z.enum(['SCORABLE', 'UNDERDETERMINED', 'NOT_DECIDABLE']);
/**
 * One shadow reviewer's opinion on one case.
 *
 * Never returned while the case is undecided. That is enforced at the read, not in the UI: an
 * operator who sees a machine's verdict before forming their own stops being an independent
 * observation, and the whole comparison collapses into a measurement of how much people defer to
 * the machine. Anchoring is not a risk to be mitigated by asking operators to ignore something on
 * their screen — it is prevented by not putting it there.
 */
export const AiReviewOpinionSchema = z
    .object({
    case_id: ReviewCaseIdSchema,
    transaction_id: TransactionIdSchema,
    /** The case reason as it stood when the opinion was formed. */
    reason: ReviewReasonSchema,
    fairness: ShadowFairnessSchema,
    /** The exact model id, e.g. `claude-opus-5`. A verdict without one cannot be compared later. */
    model: z.string().min(1),
    /** The prompt revision. Changing the prompt changes the reviewer; the number must move with it. */
    prompt_version: z.string().min(1),
    verdict: ShadowVerdictSchema,
    /** The reviewer's own stated confidence, 0–1. Calibration is itself a thing worth measuring. */
    confidence: z.number().min(0).max(1).nullable(),
    /** Why. Free text, for reading disagreements — never parsed, never scored automatically. */
    rationale: z.string().nullable(),
    answered_at: z.string().datetime({ offset: true }),
    /** Round-trip time, for cost and latency budgeting. */
    latency_ms: z.number().int().nonnegative().nullable(),
})
    .strict();
/** Agreement for one review reason. Rates are percentages to one decimal, as the dashboard does. */
export const AiReviewAgreementRowSchema = z
    .object({
    reason: ReviewReasonSchema,
    fairness: ShadowFairnessSchema,
    /** Cases in the window that a human decided AND the reviewer answered. */
    compared: z.number().int().nonnegative(),
    /** Of those, how many matched the operator's proposed decision. */
    agreed: z.number().int().nonnegative(),
    disagreed: z.number().int().nonnegative(),
    /**
     * Opinions of `ABSTAIN`, counted apart from agreement rather than folded into `disagreed`.
     *
     * An abstention on a `NOT_DECIDABLE` reason is the reviewer being right about its own limits,
     * and scoring it as a miss would reward exactly the guessing this design is trying to detect.
     */
    abstained: z.number().int().nonnegative(),
    /** `agreed / compared`, excluding abstentions. `null` when nothing was compared. */
    agreement_rate: z.number().min(0).max(100).nullable(),
    /** Mean stated confidence on the cases it did answer. `null` when it answered none. */
    mean_confidence: z.number().min(0).max(1).nullable(),
})
    .strict();
/**
 * `200` for `GET /ops/v1/ai-review/agreement`.
 *
 * There is deliberately no single headline number. The per-reason rows are the answer; a total
 * across reasons would be dominated by whichever reason happened to be most common that week.
 *
 * `human_baseline` is absent on purpose and it is worth stating why rather than leaving a reader
 * to wonder: this system has no human-versus-human number to compare against. Dual control here is
 * a ratification, not a second opinion — the confirm request body is empty and the checker copies
 * the maker's verdict verbatim, with no way to disagree. "As often as two operators agree with
 * each other" is not a sentence that can be evidenced from this data.
 */
export const AiReviewAgreementResponseSchema = z
    .object({
    rows: z.array(AiReviewAgreementRowSchema),
    /** Which reviewer produced these rows. Mixing model versions in one rate is meaningless. */
    model: z.string().min(1).nullable(),
    prompt_version: z.string().min(1).nullable(),
    /**
     * Every reviewer that answered in this window, whether or not it produced the rows above.
     *
     * Unaffected by the `model`/`prompt_version` filter on purpose — it is the menu, not the
     * selection. A window legitimately holds more than one entry while a head-to-head is running:
     * repointing `AI_REVIEW_MODEL` re-enqueues the same cases under a new
     * `(case, model, prompt_version)` key, so both reviewers answer the identical case set. When
     * that is happening, `model` above comes back `null` and this says who the two are.
     */
    reviewers: z.array(z
        .object({
        model: z.string().min(1),
        prompt_version: z.string().min(1),
        answered: z.number().int().nonnegative(),
    })
        .strict()),
    /** Opinions requested but never answered — a failure rate is part of an honest report. */
    failed: z.number().int().nonnegative(),
    /** Cases decided in the window with no opinion at all, e.g. decided before the reviewer saw them. */
    uncompared: z.number().int().nonnegative(),
    range_start: z.string().datetime({ offset: true }),
    range_end: z.string().datetime({ offset: true }),
    generated_at: z.string().datetime({ offset: true }),
})
    .strict();
/**
 * One case where the reviewer and the operator reached different conclusions.
 *
 * This is the artefact worth building the feature for. An aggregate rate says how often they
 * differ; only the pair of rationales says *why*, and why is what tells you whether the model is
 * wrong, the operator was wrong, or the case record is missing something both of them needed.
 */
export const AiReviewDisagreementRowSchema = z
    .object({
    case_id: ReviewCaseIdSchema,
    transaction_id: TransactionIdSchema,
    reason: ReviewReasonSchema,
    fairness: ShadowFairnessSchema,
    amount: MoneySchema,
    /**
     * The operator's decision — specifically the **proposal**, which is the human judgement here.
     * The confirmation cannot differ from it, so it is not a second data point.
     */
    human_decision: ReviewCaseDecisionSchema,
    /**
     * The operator's note. Shown here and *only* here.
     *
     * It is excluded from what the reviewer is given, because it is the human's reasoning written
     * before the case resolved — handing it over at inference time would leak the answer and make
     * every agreement number worthless. After the fact it is the opposite: often the only record
     * that out-of-band context (a phone call, a support ticket, the website's reply) ever existed.
     */
    human_note: z.string().nullable(),
    /** Whether a second operator ratified it — a weak signal of how settled the human answer is. */
    dual_control_confirmed: z.boolean(),
    ai_verdict: ShadowVerdictSchema,
    ai_confidence: z.number().min(0).max(1).nullable(),
    ai_rationale: z.string().nullable(),
    resolved_at: z.string().datetime({ offset: true }),
})
    .strict();
/** `200` for `GET /ops/v1/ai-review/disagreements`. */
export const AiReviewDisagreementsResponseSchema = z
    .object({
    items: z.array(AiReviewDisagreementRowSchema),
    total: z.number().int().nonnegative(),
    range_start: z.string().datetime({ offset: true }),
    range_end: z.string().datetime({ offset: true }),
    generated_at: z.string().datetime({ offset: true }),
})
    .strict();
//# sourceMappingURL=operator.js.map